import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, ShoppingBag, Sparkles, Utensils, Megaphone, Star, Settings, Loader2, Flame, Trash2, Plus, Save, KeyRound, Users, Briefcase, Shirt, Clock, UsersRound, Send, Mail, Upload, Image as ImageIcon, ClipboardList, Search, ChevronRight, Truck, Store, Package, Bell, BellOff, Eye, EyeOff, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { api, money, API } from "@/lib/api";
import { toast } from "sonner";

const ORDER_STATUSES = ["Order Received", "Order Confirmed", "In the Kitchen", "Ready for Pickup", "Out for Delivery", "Completed", "Cancelled"];

export default function AdminDashboard() {
  const { user, loading, login, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#101311] text-amber-400"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  if (!user) return <OwnerLogin login={login} navigate={navigate} />;

  const canAccess = user.is_admin || user.role === "staff";
  if (!canAccess) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0A2818] px-5 text-center">
        <div>
          <h1 className="font-display font-black text-3xl uppercase text-amber-400">Access Restricted</h1>
          <p className="text-[#FDFBF7]/70 mt-3">This dashboard is for the Sir. King Kitchen owner only.</p>
          <p className="text-[#FDFBF7]/50 text-sm mt-1">Signed in as {user.email}</p>
          <div className="flex gap-2 justify-center mt-6">
            <button onClick={logout} className="px-6 py-3 rounded-full border border-emerald-700/50 text-amber-300 font-bold">Sign Out</button>
            <button onClick={() => navigate("/")} className="px-6 py-3 rounded-full bg-amber-400 text-[#0A2818] font-bold">Back Home</button>
          </div>
        </div>
      </div>
    );
  }

  const adminTabs = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "tracker", label: "Tracker", icon: ClipboardList },
    { id: "orders", label: "Orders", icon: ShoppingBag },
    { id: "catering", label: "Catering", icon: Sparkles },
    { id: "menu", label: "Menu", icon: Utensils },
    { id: "hours", label: "Hours", icon: Clock },
    { id: "promotions", label: "Promotions", icon: Megaphone },
    { id: "reviews", label: "Reviews", icon: Star },
    { id: "chats", label: "Chats", icon: MessageCircle },
    { id: "careers", label: "Careers", icon: Briefcase },
    { id: "merch", label: "Merch", icon: Shirt },
    { id: "customers", label: "Customers", icon: UsersRound },
    { id: "settings", label: "Settings", icon: Settings },
  ];
  const staffTabs = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "tracker", label: "Tracker", icon: ClipboardList },
    { id: "orders", label: "Orders", icon: ShoppingBag },
    { id: "catering", label: "Catering", icon: Sparkles },
  ];
  const TABS = [
    ...(user.is_admin ? adminTabs : staffTabs),
    ...(user.is_owner ? [{ id: "staff", label: "Staff", icon: Users }] : []),
    { id: "account", label: "Account", icon: KeyRound },
  ];

  return (
    <div className="min-h-screen bg-[#101311]">
      <header className="sticky top-0 z-30 bg-[#0A2818] border-b border-emerald-800/40 px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display font-black text-lg"><LayoutDashboard className="w-5 h-5 text-amber-400" /> <span className="text-amber-400">Sir. King</span> Admin</div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="text-sm text-[#FDFBF7]/60 hover:text-amber-300 hidden sm:block">View Site</button>
          <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-700/50 text-amber-300 text-sm font-semibold"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-4">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)} className={`whitespace-nowrap flex items-center gap-2 px-4 py-2.5 rounded-full font-display font-bold uppercase tracking-wide text-sm border transition-colors ${tab === t.id ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-800/50 text-[#FDFBF7]/80"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "dashboard" && <DashboardTab />}
        {tab === "tracker" && <TrackerTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "catering" && <CateringTab />}
        {tab === "menu" && <MenuTab />}
        {tab === "hours" && <BusinessHoursTab />}
        {tab === "promotions" && <PromotionsTab />}
        {tab === "reviews" && <ReviewsTab />}
        {tab === "chats" && <ChatsTab />}
        {tab === "careers" && <CareersTab />}
        {tab === "merch" && <MerchTab />}
        {tab === "customers" && <CustomersTab />}
        {tab === "settings" && <SettingsTab />}
        {tab === "staff" && <StaffTab />}
        {tab === "account" && <AccountTab />}
      </div>
    </div>
  );
}

const Card = ({ children, className = "", ...rest }) => <div {...rest} className={`rounded-2xl bg-[#181D1A] border border-emerald-900/60 p-5 ${className}`}>{children}</div>;
const inp = "w-full bg-[#101311] rounded-lg px-3 py-2.5 text-sm border border-emerald-800/50 outline-none focus:border-amber-400";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const APP_STATUSES = ["New", "Reviewing", "Contacted", "Interview", "Hired", "Not Selected", "Keep on File"];

const QUICK_TEMPLATES = [
  { label: "Closed Today", subject: "We're closed today", message: "Hey! Sir. King Kitchen is closed today. We can't wait to serve you again soon — thanks for your patience and support! 🇯🇲" },
  { label: "New Menu", subject: "New on the menu! 🔥", message: "We just added new dishes to the menu — come taste what's cooking at Sir. King Kitchen. Order online anytime!" },
  { label: "Weekend Special", subject: "This weekend only 🌴", message: "A special is dropping this weekend at Sir. King Kitchen. Don't miss it — order ahead to skip the wait!" },
  { label: "Loyalty Reminder", subject: "Your free plate is waiting 🏆", message: "Reminder: with Sir. King Rewards, every plate gets you closer to a FREE one. Use the same phone number at checkout and watch it add up!" },
];

function AiDraftButton({ kind, prompt, onResult, testid, label = "Write with AI" }) {
  const { settings } = useData();
  const [busy, setBusy] = useState(false);
  if (settings?.ai_enabled !== true) return null;
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/ai/draft", { kind, prompt });
      onResult(r.data);
      toast.success("Draft ready - review before saving");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI writer unavailable");
    } finally { setBusy(false); }
  };
  return (
    <button type="button" data-testid={testid} onClick={run} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border border-amber-400/50 text-amber-300 hover:bg-amber-400/10 disabled:opacity-50 transition-colors">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {busy ? "Writing…" : label}
    </button>
  );
}

function ReviewReply({ r, onSaved }) {
  const [reply, setReply] = useState(r.owner_reply || "");
  const save = async () => {
    await api.put(`/admin/reviews/${r.review_id}/reply`, { reply });
    toast.success(reply.trim() ? "Reply saved" : "Reply removed");
    onSaved();
  };
  return (
    <div className="mt-3 space-y-2">
      <textarea data-testid={`review-reply-input-${r.review_id}`} value={reply} onChange={(e) => setReply(e.target.value)} rows={2} placeholder="Your public reply (optional)" className={inp} />
      <div className="flex flex-wrap gap-2">
        <AiDraftButton kind="review_reply" testid={`review-reply-ai-${r.review_id}`} label="Draft reply with AI" prompt={`Customer: ${r.name}\nRating: ${r.rating}/5\nReview: ${r.text}`} onResult={(d) => setReply(d.text)} />
        <button data-testid={`review-reply-save-${r.review_id}`} onClick={save} disabled={reply === (r.owner_reply || "")} className="px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-700 text-[#FDFBF7] disabled:opacity-40">Save reply</button>
      </div>
    </div>
  );
}

function ChatThread({ c, onDelete }) {
  const [open, setOpen] = useState(false);
  const when = new Date(c.last_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return (
    <Card data-testid={`chat-thread-${c.session_id}`} className="!p-4">
      <button data-testid={`chat-thread-toggle-${c.session_id}`} onClick={() => setOpen((o) => !o)} className="w-full text-left flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-bold truncate">“{c.first_question || "(no question)"}”</div>
          <div className="text-xs text-[#FDFBF7]/50 mt-0.5">{when} · {c.questions} question{c.questions === 1 ? "" : "s"}{c.unanswered > 0 && <span data-testid={`chat-thread-unanswered-${c.session_id}`} className="ml-2 px-2 py-0.5 rounded-full bg-red-900/40 text-red-300 font-bold">{c.unanswered} unanswered</span>}</div>
        </div>
        <ChevronRight className={`w-4 h-4 mt-1 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-2 border-t border-emerald-900/60 pt-3">
          {c.messages.map((m, i) => (
            <div key={`${m.at}-${i}`} className={`text-sm rounded-xl px-3 py-2 ${m.role === "user" ? (m.unanswered ? "bg-red-900/20 border border-red-500/40" : "bg-amber-400/10 border border-amber-400/30") : "bg-[#101311] text-[#FDFBF7]/75"}`}>
              <span className="text-[10px] uppercase tracking-wider font-bold text-amber-400 mr-2">{m.role === "user" ? "Customer" : "AI"}</span>{m.text.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")}
            </div>
          ))}
          <button data-testid={`chat-thread-delete-${c.session_id}`} onClick={() => onDelete(c)} className="text-xs text-red-300 flex items-center gap-1 mt-1"><Trash2 className="w-3.5 h-3.5" /> Delete conversation</button>
        </div>
      )}
    </Card>
  );
}

function UnansweredCard({ items, onResolve }) {
  return (
    <Card data-testid="chats-unanswered" className="space-y-3 !border-red-500/40">
      <div className="flex items-center gap-2">
        <h3 className="font-display font-black uppercase text-red-300">Couldn't answer</h3>
        <span data-testid="chats-unanswered-count" className="text-xs bg-red-500 text-white font-bold rounded-full px-2 py-0.5">{items.length}</span>
      </div>
      <p className="text-xs text-[#FDFBF7]/50">The AI didn't have this info. Add it to your menu, Settings or FAQs, then mark it handled.</p>
      {items.length === 0 ? <p data-testid="chats-unanswered-empty" className="text-sm text-[#FDFBF7]/40">Nothing to fix - the AI answered everything.</p> : (
        <div className="space-y-2">
          {items.map((q) => (
            <div key={q.msg_id} data-testid={`unanswered-${q.msg_id}`} className="flex items-start justify-between gap-3 rounded-xl bg-[#101311] border border-red-500/20 px-3 py-2">
              <div className="min-w-0"><div className="text-sm">“{q.text}”</div><div className="text-[11px] text-[#FDFBF7]/40">{new Date(q.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div></div>
              <button data-testid={`unanswered-resolve-${q.msg_id}`} onClick={() => onResolve(q)} className="shrink-0 px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-700 text-[#FDFBF7]">Mark handled</button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function ChatsTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/chats", { params: { days } });
      setData(r.data);
    } catch {
      toast.error("Could not load chats");
    }
  }, [days]);
  useEffect(() => { load(); }, [load]);
  const del = async (c) => {
    if (!window.confirm("Delete this conversation?")) return;
    await api.delete(`/admin/chats/${c.session_id}`);
    toast.success("Conversation deleted");
    load();
  };
  const resolve = async (q) => {
    await api.put(`/admin/chats/unanswered/${q.msg_id}/resolve`);
    toast.success("Marked as handled");
    load();
  };
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto mt-10" />;
  return (
    <div className="space-y-4" data-testid="chats-tab">
      <Card className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display font-black text-lg uppercase flex items-center gap-2"><MessageCircle className="w-5 h-5 text-amber-400" /> Customer Chat Inbox</h2>
          <div className="flex gap-1">
            {[7, 30, 90].map((d) => (
              <button key={d} data-testid={`chats-range-${d}`} onClick={() => setDays(d)} className={`px-3 py-1.5 rounded-full text-xs font-bold border ${days === d ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-800/50 text-[#FDFBF7]/70"}`}>{d} days</button>
            ))}
          </div>
        </div>
        <div data-testid="chats-summary" className="text-sm text-[#FDFBF7]/70"><b className="text-[#FDFBF7]">{data.total_conversations}</b> conversations · <b className="text-[#FDFBF7]">{data.total_questions}</b> questions</div>
        <div>
          <div className="text-xs uppercase tracking-wider text-amber-400 font-bold mb-2">What people ask about most</div>
          {data.top_topics.length === 0 ? <p className="text-sm text-[#FDFBF7]/40">No questions yet.</p> : (
            <div className="flex flex-wrap gap-2">
              {data.top_topics.map((t) => (
                <span key={t.word} data-testid={`chats-topic-${t.word}`} className="px-3 py-1.5 rounded-full bg-emerald-900/40 border border-emerald-700/50 text-sm capitalize">{t.word} <b className="text-amber-300">{t.count}</b></span>
              ))}
            </div>
          )}
        </div>
      </Card>
      <UnansweredCard items={data.unanswered || []} onResolve={resolve} />
      {data.conversations.length === 0 && <p data-testid="chats-empty" className="text-center text-[#FDFBF7]/40 py-8">No customer chats in this period.</p>}
      <div className="space-y-2">
        {data.conversations.map((c) => <ChatThread key={c.session_id} c={c} onDelete={del} />)}
      </div>
    </div>
  );
}

function CustomersTab() {
  const [data, setData] = useState({ customers: [], sms_configured: false, email_configured: true, total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({});
  const [audience, setAudience] = useState("all");
  const [subject, setSubject] = useState("");
  const [aiIdea, setAiIdea] = useState("");
  const [message, setMessage] = useState("");
  const [btnLabel, setBtnLabel] = useState("");
  const [btnUrl, setBtnUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [scheduleFor, setScheduleFor] = useState("");
  const [scheduled, setScheduled] = useState([]);
  const [alertEmails, setAlertEmails] = useState([]);
  const [alertInput, setAlertInput] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/customers").then((r) => setData(r.data)).catch(() => toast.error("Could not load customers")).finally(() => setLoading(false));
  }, []);
  const loadHistory = useCallback(() => { api.get("/admin/broadcasts").then((r) => setHistory(r.data)).catch(() => toast.error("Could not load broadcast history")); }, []);
  const loadScheduled = useCallback(() => { api.get("/admin/scheduled-broadcasts").then((r) => setScheduled(r.data)).catch(() => toast.error("Could not load scheduled broadcasts")); }, []);
  const loadAlerts = useCallback(() => { api.get("/admin/alert-recipients").then((r) => setAlertEmails(r.data.emails || [])).catch(() => toast.error("Could not load alert recipients")); }, []);
  useEffect(() => { load(); loadHistory(); loadScheduled(); loadAlerts(); }, [load, loadHistory, loadScheduled, loadAlerts]);

  const filtered = data.customers.filter((c) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (c.name || "").toLowerCase().includes(s) || (c.email || "").includes(s) || (c.phone || "").includes(s);
  });
  const selectedList = data.customers.filter((c) => selected[c.email]);
  const selectedCount = selectedList.length;
  const allFilteredSelected = filtered.length > 0 && filtered.every((c) => selected[c.email]);
  const toggleAll = () => {
    const next = { ...selected };
    if (allFilteredSelected) filtered.forEach((c) => delete next[c.email]);
    else filtered.forEach((c) => { next[c.email] = true; });
    setSelected(next);
  };
  const fmtDate = (x) => { try { return x ? new Date(x).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"; } catch { return "—"; } };
  const fmtDateTime = (x) => { try { return x ? new Date(x).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"; } catch { return "—"; } };
  const cancelScheduled = async (id) => { await api.delete(`/admin/scheduled-broadcasts/${id}`); toast.success("Scheduled update cancelled."); loadScheduled(); };
  const addAlert = async () => { const e = alertInput.trim().toLowerCase(); if (!e || !e.includes("@")) return toast.error("Enter a valid email."); const next = [...new Set([...alertEmails, e])]; const { data } = await api.put("/admin/alert-recipients", { emails: next }); setAlertEmails(data.emails); setAlertInput(""); toast.success("Alert recipient added."); };
  const removeAlert = async (e) => { const next = alertEmails.filter((x) => x !== e); const { data } = await api.put("/admin/alert-recipients", { emails: next }); setAlertEmails(data.emails); toast.success("Removed."); };

  const send = async () => {
    if (!message.trim()) return toast.error("Please write a message.");
    if (audience === "selected" && selectedCount === 0) return toast.error("Select at least one customer, or choose 'All customers'.");
    setSending(true);
    try {
      const payload = { channel: "email", audience, subject, message, button_label: btnLabel || null, button_url: btnUrl || null };
      if (scheduleFor) payload.scheduled_for = new Date(scheduleFor).toISOString();
      if (audience === "selected") payload.recipients = selectedList.map((c) => ({ email: c.email, phone: c.phone, name: c.name }));
      const { data: res } = await api.post("/admin/broadcast", payload);
      if (res.scheduled) {
        toast.success("Scheduled! It will send automatically at the time you picked.");
        loadScheduled();
      } else {
        const n = res.email_sent || 0;
        toast.success(n ? `Sent ${n} email${n > 1 ? "s" : ""}!` : (res.email_failed ? "Some emails could not be delivered." : "Broadcast processed."));
        loadHistory();
      }
      setMessage(""); setSubject(""); setBtnLabel(""); setBtnUrl(""); setScheduleFor("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the broadcast.");
    } finally { setSending(false); }
  };

  const SourceBadge = ({ s }) => {
    const map = { account: ["Account", "bg-emerald-900/70 text-emerald-300 border-emerald-600/40"], both: ["Account", "bg-emerald-900/70 text-emerald-300 border-emerald-600/40"], guest: ["Guest", "bg-amber-900/40 text-amber-300 border-amber-600/40"] };
    const [label, cls] = map[s] || map.guest;
    return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${cls}`}>{label}</span>;
  };

  return (
    <div className="space-y-5" data-testid="customers-tab">
      <Card>
        <div className="flex items-center gap-2 mb-3"><Send className="w-5 h-5 text-amber-400" /><h2 className="font-display font-black text-xl uppercase">Send an Update</h2></div>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-wider text-amber-400 font-bold">Send to</div>
            <div className="flex gap-2">
              <button data-testid="broadcast-audience-all" onClick={() => setAudience("all")} className={`flex-1 py-2.5 rounded-lg text-sm font-bold border min-h-[44px] ${audience === "all" ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-800/50 text-[#FDFBF7]/80"}`}>All customers ({data.total})</button>
              <button data-testid="broadcast-audience-selected" onClick={() => setAudience("selected")} className={`flex-1 py-2.5 rounded-lg text-sm font-bold border min-h-[44px] ${audience === "selected" ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-800/50 text-[#FDFBF7]/80"}`}>Selected ({selectedCount})</button>
            </div>
            <div className="text-xs uppercase tracking-wider text-amber-400 font-bold pt-1 flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> Delivered by Email</div>
            <div className="text-xs uppercase tracking-wider text-amber-400 font-bold pt-1">Quick Templates</div>
            <div className="flex flex-wrap gap-2">
              {QUICK_TEMPLATES.map((t) => (
                <button key={t.label} data-testid={`broadcast-template-${t.label.toLowerCase().replace(/[^a-z]/g, "-")}`} onClick={() => { setSubject(t.subject); setMessage(t.message); }} className="px-3 py-2 rounded-full text-xs font-bold border border-emerald-800/50 text-[#FDFBF7]/80 hover:border-amber-400 min-h-[36px]">{t.label}</button>
              ))}
            </div>
            <div className="text-xs uppercase tracking-wider text-amber-400 font-bold pt-1">Or write it with AI</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input data-testid="broadcast-ai-idea" value={aiIdea} onChange={(e) => setAiIdea(e.target.value)} placeholder="What's it about? e.g. oxtail special this Saturday" className={inp} />
              <AiDraftButton kind="broadcast" testid="broadcast-ai-button" prompt={aiIdea} onResult={(d) => { if (d.subject) setSubject(d.subject); setMessage(d.message); }} />
            </div>
          </div>
          <div className="space-y-2">
            <input data-testid="broadcast-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (email headline)" className={inp} />
            <textarea data-testid="broadcast-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="Write your update… (e.g. New menu drop this weekend! 🔥)" className={inp} />
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="broadcast-button-label" value={btnLabel} onChange={(e) => setBtnLabel(e.target.value)} placeholder="Button text (optional)" className={inp} />
              <input data-testid="broadcast-button-url" value={btnUrl} onChange={(e) => setBtnUrl(e.target.value)} placeholder="Button link (optional)" className={inp} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-amber-400 font-bold">Send later (optional)</label>
              <input data-testid="broadcast-schedule" type="datetime-local" value={scheduleFor} onChange={(e) => setScheduleFor(e.target.value)} className={inp} />
            </div>
            <button data-testid="broadcast-send-button" onClick={send} disabled={sending} className="w-full py-3.5 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-40 text-[#0A2818] font-display font-black uppercase tracking-wide flex items-center justify-center gap-2 active:scale-95 transition-transform min-h-[44px]">
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} {scheduleFor ? "Schedule" : "Send Email"}{audience === "selected" ? ` to ${selectedCount}` : ""}
            </button>
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex items-center gap-2 mb-2"><Mail className="w-5 h-5 text-amber-400" /><h2 className="font-display font-black text-lg uppercase">New-Order Alert Emails</h2></div>
        <p className="text-xs text-[#FDFBF7]/50 mb-3">Your owner email always gets alerts. Add staff or extra emails to also be notified the moment an order is paid.</p>
        <div className="flex gap-2 mb-2">
          <input data-testid="alert-email-input" type="email" value={alertInput} onChange={(e) => setAlertInput(e.target.value)} placeholder="staff@example.com" className={inp} />
          <button data-testid="alert-email-add" onClick={addAlert} className="px-4 rounded-lg bg-amber-400 text-[#0A2818] font-bold shrink-0 min-h-[44px]">Add</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {alertEmails.map((e) => (
            <span key={e} data-testid={`alert-email-${e}`} className="inline-flex items-center gap-2 text-xs bg-emerald-900/60 border border-emerald-700/40 rounded-full pl-3 pr-2 py-1">{e}<button onClick={() => removeAlert(e)} className="text-red-300 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button></span>
          ))}
          {alertEmails.length === 0 && <span className="text-xs text-[#FDFBF7]/40">No extra recipients yet.</span>}
        </div>
      </Card>

      {scheduled.length > 0 && (
        <Card>
          <div className="flex items-center gap-2 mb-3"><Clock className="w-5 h-5 text-amber-400" /><h2 className="font-display font-black text-lg uppercase">Scheduled Updates</h2></div>
          <div className="space-y-2">
            {scheduled.map((b) => (
              <div key={b.broadcast_id} data-testid={`scheduled-${b.broadcast_id}`} className="flex items-center justify-between gap-3 rounded-xl border border-amber-700/30 bg-[#101311] p-3">
                <div className="min-w-0">
                  <div className="font-display font-bold truncate">{b.subject}</div>
                  <div className="text-[11px] text-amber-300/80">Sends {fmtDateTime(b.scheduled_for)} · {b.audience === "all" ? "All customers" : "Selected"}</div>
                </div>
                <button data-testid={`scheduled-cancel-${b.broadcast_id}`} onClick={() => cancelScheduled(b.broadcast_id)} className="text-xs text-red-300 hover:text-red-400 font-bold shrink-0">Cancel</button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {history.length > 0 && (
        <Card>
          <div className="flex items-center gap-2 mb-3"><Megaphone className="w-5 h-5 text-amber-400" /><h2 className="font-display font-black text-xl uppercase">Recent Updates</h2></div>
          <div className="space-y-2">
            {history.map((b) => (
              <div key={b.broadcast_id} data-testid={`broadcast-history-${b.broadcast_id}`} className="flex items-center justify-between gap-3 rounded-xl border border-emerald-900/60 bg-[#101311] p-3">
                <div className="min-w-0">
                  <div className="font-display font-bold truncate">{b.subject}</div>
                  <div className="text-[11px] text-[#FDFBF7]/50">{fmtDate(b.created_at)} · {b.audience === "all" ? "All customers" : "Selected"}</div>
                </div>
                <div className="text-right shrink-0 text-xs">
                  <div className="text-amber-400 font-bold">{b.result?.email_sent ?? 0} sent</div>
                  <div className="text-[#FDFBF7]/45">{b.opens ?? 0} opened</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2"><UsersRound className="w-5 h-5 text-amber-400" /><h2 className="font-display font-black text-xl uppercase">Customers</h2><span className="text-[#FDFBF7]/50 text-sm">({data.total})</span></div>
          <input data-testid="customers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone" className={`${inp} max-w-[260px]`} />
        </div>
        {loading ? (
          <div className="py-10 grid place-items-center text-amber-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-[#FDFBF7]/50 py-10">No customers yet.</p>
        ) : (
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs text-[#FDFBF7]/60 pb-1 cursor-pointer">
              <input data-testid="customers-select-all" type="checkbox" checked={allFilteredSelected} onChange={toggleAll} className="accent-amber-400 w-4 h-4" /> Select all shown
            </label>
            {filtered.map((c) => (
              <div key={c.email} data-testid={`customer-row-${c.email}`} className="flex items-center gap-3 rounded-xl border border-emerald-900/60 bg-[#101311] p-3">
                <input data-testid={`customer-select-${c.email}`} type="checkbox" checked={!!selected[c.email]} onChange={(e) => setSelected((p) => ({ ...p, [c.email]: e.target.checked }))} className="accent-amber-400 w-5 h-5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold truncate">{c.name || "Guest"}</span>
                    <SourceBadge s={c.source} />
                  </div>
                  <div className="text-xs text-[#FDFBF7]/60 truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold text-amber-400">{c.orders_count} order{c.orders_count === 1 ? "" : "s"}</div>
                  <div className="text-[11px] text-[#FDFBF7]/45">{fmtDate(c.last_order_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function CareersTab() {
  const [sub, setSub] = useState("applications");
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filterJob, setFilterJob] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const loadJobs = useCallback(async () => { const r = await api.get("/admin/careers/jobs"); setJobs(r.data); }, []);
  const loadApps = useCallback(async () => {
    const params = {};
    if (filterJob) params.job_id = filterJob;
    if (filterStatus) params.status = filterStatus;
    if (q) params.q = q;
    const r = await api.get("/admin/careers/applications", { params });
    setApps(r.data);
  }, [filterJob, filterStatus, q]);

  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => { loadApps(); }, [loadApps]);

  const blankJob = () => ({ title: "", location: "Sir. King Kitchen, Maine", employment_type: "Part-Time / Flexible", compensation: "", description: "", requirements: [], responsibilities: [], schedule: "", deadline: "", highlights: [], active: true, order: 0 });
  const saveApp = async (id, patch) => { await api.put(`/admin/careers/applications/${id}`, patch); toast.success("Updated"); loadApps(); if (detail?.application_id === id) setDetail({ ...detail, ...patch }); };
  const delApp = async (id) => { if (!window.confirm("Delete this application?")) return; await api.delete(`/admin/careers/applications/${id}`); toast.success("Deleted"); setDetail(null); loadApps(); };
  const avail = (a) => Object.entries(a.availability || {}).filter(([, v]) => v?.length).map(([d, v]) => `${d}: ${v.join(", ")}`).join(" · ") || "Flexible / not specified";

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <button data-testid="careers-sub-applications" onClick={() => setSub("applications")} className={`px-4 py-2 rounded-full text-sm font-bold ${sub === "applications" ? "bg-amber-400 text-[#0A2818]" : "border border-emerald-800/50 text-[#FDFBF7]/80"}`}>Applications</button>
        <button data-testid="careers-sub-jobs" onClick={() => setSub("jobs")} className={`px-4 py-2 rounded-full text-sm font-bold ${sub === "jobs" ? "bg-amber-400 text-[#0A2818]" : "border border-emerald-800/50 text-[#FDFBF7]/80"}`}>Job Postings</button>
      </div>

      {sub === "jobs" && (
        <div>
          <button data-testid="admin-add-job" onClick={() => setEditing(blankJob())} className="mb-4 flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase text-sm"><Plus className="w-4 h-4" /> Add Job</button>
          {!jobs.length ? <Card><p className="text-[#FDFBF7]/60 text-center py-6">No job postings yet.</p></Card> : (
            <div className="space-y-3">
              {jobs.map((j) => (
                <Card key={j.job_id}>
                  <div className="flex flex-wrap justify-between gap-3">
                    <div>
                      <div className="font-display font-black text-lg">{j.title} {j.active ? <span className="text-emerald-400 text-xs">● Active</span> : <span className="text-red-400 text-xs">● Closed</span>}</div>
                      <div className="text-sm text-[#FDFBF7]/60">{j.employment_type} · {j.location}</div>
                      <div className="text-sm text-amber-300 mt-1">{j.compensation}</div>
                    </div>
                    <div className="flex gap-2 items-start">
                      <button data-testid={`admin-edit-job-${j.job_id}`} onClick={() => setEditing(j)} className="text-xs px-3 py-1.5 rounded-full border border-emerald-700/50">Edit</button>
                      <button onClick={() => saveJobActive(j, !j.active, loadJobs)} className="text-xs px-3 py-1.5 rounded-full border border-emerald-700/50">{j.active ? "Close" : "Reopen"}</button>
                      <button onClick={async () => { if (window.confirm(`Delete ${j.title}?`)) { await api.delete(`/admin/careers/jobs/${j.job_id}`); toast.success("Deleted"); loadJobs(); } }} className="text-xs px-3 py-1.5 rounded-full border border-red-800/50 text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
          {editing && <JobEditor job={editing} jobs={jobs} onClose={() => setEditing(null)} onSaved={loadJobs} />}
        </div>
      )}

      {sub === "applications" && (
        <div>
          <div className="flex flex-wrap gap-2 mb-4">
            <input data-testid="app-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone" className={`${inp} max-w-[240px]`} />
            <select data-testid="app-filter-job" value={filterJob} onChange={(e) => setFilterJob(e.target.value)} className={`${inp} max-w-[200px]`}><option value="">All positions</option>{jobs.map((j) => <option key={j.job_id} value={j.job_id}>{j.title}</option>)}</select>
            <select data-testid="app-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={`${inp} max-w-[180px]`}><option value="">All statuses</option>{APP_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
          </div>
          {!apps.length ? <Card><p className="text-[#FDFBF7]/60 text-center py-6">No applications yet.</p></Card> : (
            <div className="space-y-3">
              {apps.map((a) => (
                <Card key={a.application_id}>
                  <div className="flex flex-wrap justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-display font-black text-lg">{a.first_name} {a.last_name} <span className="text-xs text-amber-300">· {a.job_title}</span></div>
                      <div className="text-sm text-[#FDFBF7]/60">{a.phone} · {a.email} · {a.city}</div>
                      <div className="text-xs text-[#FDFBF7]/45 mt-1">{(a.created_at || "").slice(0, 10)} · {avail(a)}</div>
                      <div className="flex gap-2 mt-2">
                        <a data-testid={`app-call-${a.application_id}`} href={`tel:${(a.phone || "").replace(/[^0-9]/g, "")}`} className="text-xs px-3 py-1.5 rounded-full border border-emerald-700/50">Call</a>
                        <a data-testid={`app-text-${a.application_id}`} href={`sms:${(a.phone || "").replace(/[^0-9]/g, "")}`} className="text-xs px-3 py-1.5 rounded-full border border-emerald-700/50">Text</a>
                        <a data-testid={`app-email-${a.application_id}`} href={`mailto:${a.email}`} className="text-xs px-3 py-1.5 rounded-full border border-emerald-700/50">Email</a>
                        <button onClick={() => setDetail(a)} className="text-xs px-3 py-1.5 rounded-full border border-amber-500/50 text-amber-300">View</button>
                      </div>
                    </div>
                    <select data-testid={`app-status-${a.application_id}`} value={a.status} onChange={(e) => saveApp(a.application_id, { status: e.target.value })} className={`${inp} max-w-[170px] self-start`}>{APP_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
                  </div>
                </Card>
              ))}
            </div>
          )}
          {detail && <AppDetail a={detail} avail={avail} onClose={() => setDetail(null)} onSave={saveApp} onDelete={delApp} />}
        </div>
      )}
    </div>
  );
}

async function saveJobActive(job, active, reload) {
  await api.put(`/admin/careers/jobs/${job.job_id}`, { ...job, active });
  toast.success(active ? "Reopened" : "Closed");
  reload();
}

function AppDetail({ a, avail, onClose, onSave, onDelete }) {
  const [notes, setNotes] = useState(a.internal_notes || "");
  const yn = (v) => (v === true ? "Yes" : v === false ? "No" : "—");
  return (
    <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-[#181D1A] border border-emerald-800/60 rounded-2xl max-w-lg w-full max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="app-detail-modal">
        <div className="flex justify-between items-start"><h3 className="font-display font-black text-xl">{a.first_name} {a.last_name}</h3><button onClick={onClose} className="text-[#FDFBF7]/50">✕</button></div>
        <p className="text-sm text-amber-300">{a.job_title} · {a.status}</p>
        <div className="text-sm text-[#FDFBF7]/80 mt-3 space-y-1">
          <div>📞 {a.phone} · ✉️ {a.email}</div>
          <div>📍 {a.city}</div>
          <div>License: {yn(a.has_license)} · Transport: {yn(a.has_transport)} · Record: {a.driving_record || "—"}</div>
          <div>Availability: {avail(a)}</div>
          <div>Hours/week: {a.hours_per_week || "—"}</div>
          <div>Prior experience: {yn(a.prior_experience)}</div>
          {a.experience_text && <div className="italic text-[#FDFBF7]/60">"{a.experience_text}"</div>}
          {a.additional_comments && <div className="italic text-[#FDFBF7]/60">Notes: "{a.additional_comments}"</div>}
          {a.resume_path ? (
            <a data-testid="app-download-resume" href={`${API}/admin/careers/applications/${a.application_id}/resume`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-full bg-amber-400 text-[#0A2818] font-bold text-xs uppercase">📄 Download Resume{a.resume_name ? ` (${a.resume_name})` : ""}</a>
          ) : <div className="text-[#FDFBF7]/40 text-xs mt-2">No resume attached</div>}
        </div>
        <div className="mt-4">
          <label className="text-xs uppercase tracking-wide text-amber-400 font-bold">Internal Notes (private)</label>
          <textarea data-testid="app-internal-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={`${inp} mt-1`} />
          <div className="flex gap-2 mt-3">
            <button data-testid="app-save-notes" onClick={() => onSave(a.application_id, { internal_notes: notes })} className="flex-1 py-2.5 rounded-full bg-amber-400 text-[#0A2818] font-bold text-sm">Save Notes</button>
            <button onClick={() => onDelete(a.application_id)} className="px-4 py-2.5 rounded-full border border-red-800/50 text-red-400 text-sm"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

function JobEditor({ job, onClose, onSaved }) {
  const [f, setF] = useState({ ...job, requirements: (job.requirements || []).join("\n"), responsibilities: (job.responsibilities || []).join("\n"), highlights: (job.highlights || []).join(", ") });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    const payload = { ...f, order: Number(f.order) || 0,
      requirements: String(f.requirements).split("\n").map((x) => x.trim()).filter(Boolean),
      responsibilities: String(f.responsibilities).split("\n").map((x) => x.trim()).filter(Boolean),
      highlights: String(f.highlights).split(",").map((x) => x.trim()).filter(Boolean) };
    if (job.job_id) await api.put(`/admin/careers/jobs/${job.job_id}`, payload);
    else await api.post("/admin/careers/jobs", payload);
    toast.success("Saved"); await onSaved(); onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-[#181D1A] border border-emerald-800/60 rounded-2xl max-w-lg w-full max-h-[88vh] overflow-y-auto p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="job-editor-modal">
        <h3 className="font-display font-black text-xl">{job.job_id ? "Edit" : "Add"} Job</h3>
        <input data-testid="job-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Job Title" className={inp} />
        <input value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="Location" className={inp} />
        <input value={f.employment_type} onChange={(e) => set("employment_type", e.target.value)} placeholder="Employment Type" className={inp} />
        <input value={f.compensation} onChange={(e) => set("compensation", e.target.value)} placeholder="Compensation / Pay" className={inp} />
        <textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} placeholder="Description" className={inp} />
        <textarea value={f.requirements} onChange={(e) => set("requirements", e.target.value)} rows={4} placeholder="Requirements (one per line)" className={inp} />
        <textarea value={f.responsibilities} onChange={(e) => set("responsibilities", e.target.value)} rows={3} placeholder="Responsibilities (one per line)" className={inp} />
        <input value={f.highlights} onChange={(e) => set("highlights", e.target.value)} placeholder="Card highlights (comma-separated)" className={inp} />
        <input value={f.schedule} onChange={(e) => set("schedule", e.target.value)} placeholder="Schedule / Availability" className={inp} />
        <input value={f.deadline || ""} onChange={(e) => set("deadline", e.target.value)} placeholder="Application deadline (optional)" className={inp} />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} /> Active (accepting applications)</label>
        <div className="flex gap-2 pt-2">
          <button data-testid="job-save" onClick={save} className="flex-1 py-3 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase text-sm">Save</button>
          <button onClick={onClose} className="px-5 py-3 rounded-full border border-emerald-700/50 text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

const MERCH_FSTATUS = ["Awaiting Payment", "Paid", "Processing", "In Production", "Shipped", "Delivered", "Cancelled", "Refunded"];

function MerchTab() {
  const [sub, setSub] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [pf, setPf] = useState(null);
  const [pfBusy, setPfBusy] = useState("");

  const loadP = useCallback(async () => { const r = await api.get("/admin/merch/products"); setProducts(r.data); }, []);
  const loadO = useCallback(async () => { const r = await api.get("/admin/merch/orders"); setOrders(r.data); }, []);
  const loadPf = useCallback(() => api.get("/admin/merch/printful-status").then((r) => setPf(r.data)).catch(() => setPf({ connected: false, reason: "check failed" })), []);
  useEffect(() => { loadP(); loadO(); loadPf(); }, [loadP, loadO, loadPf]);

  const syncNow = async () => {
    setPfBusy("sync");
    try { const r = await api.post("/admin/merch/sync-printful"); toast.success(`Synced ${r.data.synced} product(s) from Printful`); await loadP(); await loadPf(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setPfBusy(""); }
  };
  const registerHook = async () => {
    setPfBusy("hook");
    try { await api.post("/admin/merch/printful-register-webhook", {}); toast.success("Tracking webhook registered"); await loadPf(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Webhook registration failed"); }
    finally { setPfBusy(""); }
  };

  const blank = () => ({ name: "", collection: "T-Shirts", description: "", retail_price: 29.99, base_cost: 12, shipping_est: 4.99, sizes: ["S", "M", "L", "XL"], colors: ["Black"], images: [], featured: false, limited_edition: false, active: true, order: 99 });
  const del = async (p) => { if (!window.confirm(`Delete ${p.name}?`)) return; await api.delete(`/admin/merch/products/${p.product_id}`); toast.success("Deleted"); loadP(); };
  const saveOrder = async (id, patch) => { await api.put(`/admin/merch/orders/${id}/status`, patch); toast.success("Updated"); loadO(); };

  return (
    <div>
      <div className={`mb-4 rounded-xl p-4 text-sm border ${pf?.connected ? "border-emerald-600/50 bg-emerald-950/30 text-emerald-300" : "border-amber-600/50 bg-amber-950/20 text-amber-300"}`} data-testid="merch-printful-status">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {pf == null ? "Checking Printful connection…" : pf.connected ? (
              <>
                <div className="font-bold">✓ Printful connected — {pf.store}</div>
                <div className="text-xs mt-1 text-[#FDFBF7]/70">
                  {pf.printful_products} product(s) in Printful · {pf.synced_products} synced to store · Auto-tracking {pf.webhook_url ? "ON ✓" : "not set up yet"}
                </div>
                <div className="text-xs mt-1 text-[#FDFBF7]/50">Add/design products in Printful, then hit <b>Sync from Printful</b>. Paid orders are auto-produced & shipped; tracking is emailed to customers automatically.</div>
              </>
            ) : `Printful not fully connected: ${pf.reason}`}
          </div>
          {pf?.connected && (
            <div className="flex flex-col gap-2 shrink-0">
              <button data-testid="merch-sync-printful" onClick={syncNow} disabled={!!pfBusy} className="px-4 py-2 rounded-full bg-amber-400 text-[#0A2818] font-bold text-xs uppercase disabled:opacity-50">{pfBusy === "sync" ? "Syncing…" : "Sync from Printful"}</button>
              <button data-testid="merch-register-webhook" onClick={registerHook} disabled={!!pfBusy} className="px-4 py-2 rounded-full border border-emerald-600/60 text-emerald-300 font-bold text-xs uppercase disabled:opacity-50">{pfBusy === "hook" ? "Registering…" : (pf.webhook_url ? "Re-register Tracking" : "Enable Auto-Tracking")}</button>
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2 mb-4">
        <button data-testid="merch-sub-products" onClick={() => setSub("products")} className={`px-4 py-2 rounded-full text-sm font-bold ${sub === "products" ? "bg-amber-400 text-[#0A2818]" : "border border-emerald-800/50 text-[#FDFBF7]/80"}`}>Products</button>
        <button data-testid="merch-sub-orders" onClick={() => setSub("orders")} className={`px-4 py-2 rounded-full text-sm font-bold ${sub === "orders" ? "bg-amber-400 text-[#0A2818]" : "border border-emerald-800/50 text-[#FDFBF7]/80"}`}>Orders</button>
      </div>

      {sub === "products" && (
        <div>
          <button data-testid="admin-add-merch" onClick={() => setEditing(blank())} className="mb-4 flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase text-sm"><Plus className="w-4 h-4" /> Add Product</button>
          <div className="grid sm:grid-cols-2 gap-3">
            {products.map((p) => {
              const profit = (Number(p.retail_price) - Number(p.base_cost || 0)).toFixed(2);
              return (
                <Card key={p.product_id} className="flex gap-3">
                  <img src={p.images?.[0]} alt={p.name} className="w-16 h-16 rounded-lg object-cover bg-[#101311]" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold truncate">{p.name} {!p.active && <span className="text-red-400 text-xs">· hidden</span>}{p.featured && <span className="text-amber-400 text-xs"> · ★</span>}{p.source === "printful" ? <span className="text-emerald-400 text-xs"> · Printful ✓</span> : <span className="text-amber-400/70 text-xs"> · not linked</span>}</div>
                    <div className="text-xs text-[#FDFBF7]/50">{p.collection}</div>
                    <div className="text-xs text-[#FDFBF7]/70 mt-1">Retail <b className="text-amber-400">{money(p.retail_price)}</b> · Cost {money(p.base_cost || 0)} · Profit <b className="text-emerald-400">${profit}</b></div>
                    <div className="flex gap-2 mt-2">
                      <button data-testid={`admin-edit-merch-${p.product_id}`} onClick={() => setEditing(p)} className="text-xs px-3 py-1.5 rounded-full border border-emerald-700/50">Edit</button>
                      <button onClick={() => del(p)} className="text-xs px-3 py-1.5 rounded-full border border-red-800/50 text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
          {editing && <MerchEditor product={editing} onClose={() => setEditing(null)} onSaved={loadP} />}
        </div>
      )}

      {sub === "orders" && (
        !orders.length ? <Card><p className="text-[#FDFBF7]/60 text-center py-6">No merch orders yet.</p></Card> : (
          <div className="space-y-3">
            {orders.map((o) => (
              <Card key={o.order_id}>
                <div className="flex flex-wrap justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display font-black text-lg">#{o.order_number} · {money(o.total)} <span className={`text-xs ${o.payment_status === "paid" ? "text-emerald-400" : "text-amber-400"}`}>· {o.payment_status}</span></div>
                    <div className="text-sm text-[#FDFBF7]/60">{o.customer_name} · {o.customer_email}</div>
                    <div className="text-xs text-[#FDFBF7]/50">{o.address} {o.city} {o.state} {o.zip_code}</div>
                    <div className="text-sm text-[#FDFBF7]/70 mt-1">{o.items.map((i) => `${i.quantity}× ${i.name}${i.size ? ` (${i.size}${i.color ? "/" + i.color : ""})` : ""}`).join(", ")}</div>
                    <input data-testid={`merch-tracking-${o.order_number}`} defaultValue={o.tracking_number} onBlur={(e) => e.target.value !== (o.tracking_number || "") && saveOrder(o.order_id, { tracking_number: e.target.value })} placeholder="Tracking number" className={`${inp} max-w-[260px] mt-2`} />
                  </div>
                  <select data-testid={`merch-status-${o.order_number}`} value={o.fulfillment_status || "Awaiting Payment"} onChange={(e) => saveOrder(o.order_id, { fulfillment_status: e.target.value })} className={`${inp} max-w-[180px] self-start`}>{MERCH_FSTATUS.map((s) => <option key={s}>{s}</option>)}</select>
                </div>
              </Card>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function MerchEditor({ product, onClose, onSaved }) {
  const [f, setF] = useState({ ...product, sizes: (product.sizes || []).join(", "), colors: (product.colors || []).join(", "), images: (product.images || []).join(", ") });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    const payload = { ...f, retail_price: Number(f.retail_price), base_cost: Number(f.base_cost) || 0, shipping_est: Number(f.shipping_est) || 0, order: Number(f.order) || 0,
      sizes: String(f.sizes).split(",").map((x) => x.trim()).filter(Boolean), colors: String(f.colors).split(",").map((x) => x.trim()).filter(Boolean), images: String(f.images).split(",").map((x) => x.trim()).filter(Boolean) };
    if (product.product_id) await api.put(`/admin/merch/products/${product.product_id}`, payload);
    else await api.post("/admin/merch/products", payload);
    toast.success("Saved"); await onSaved(); onClose();
  };
  const profit = (Number(f.retail_price) - Number(f.base_cost || 0)).toFixed(2);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-[#181D1A] border border-emerald-800/60 rounded-2xl max-w-lg w-full max-h-[88vh] overflow-y-auto p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="merch-editor-modal">
        <h3 className="font-display font-black text-xl">{product.product_id ? "Edit" : "Add"} Merch Product</h3>
        <input data-testid="merch-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Product name" className={inp} />
        <select value={f.collection} onChange={(e) => set("collection", e.target.value)} className={inp}>{["T-Shirts", "Hoodies & Sweatshirts", "Hats", "Tote Bags", "Drinkware", "Accessories", "Limited Edition"].map((c) => <option key={c}>{c}</option>)}</select>
        <textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={2} placeholder="Description" className={inp} />
        <div className="grid grid-cols-3 gap-2">
          <div><label className="text-[11px] text-amber-400">Retail $</label><input type="number" step="0.01" value={f.retail_price} onChange={(e) => set("retail_price", e.target.value)} className={inp} /></div>
          <div><label className="text-[11px] text-[#FDFBF7]/50">Cost $ (private)</label><input type="number" step="0.01" value={f.base_cost} onChange={(e) => set("base_cost", e.target.value)} className={inp} /></div>
          <div><label className="text-[11px] text-emerald-400">Profit</label><div className={`${inp} bg-[#0A2818]`}>${profit}</div></div>
        </div>
        <input value={f.sizes} onChange={(e) => set("sizes", e.target.value)} placeholder="Sizes (comma-separated)" className={inp} />
        <input value={f.colors} onChange={(e) => set("colors", e.target.value)} placeholder="Colors (comma-separated)" className={inp} />
        <input value={f.images} onChange={(e) => set("images", e.target.value)} placeholder="Image URLs (comma-separated)" className={inp} />
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.limited_edition} onChange={(e) => set("limited_edition", e.target.checked)} /> Limited Edition</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} /> Active</label>
        </div>
        <div className="flex gap-2 pt-2">
          <button data-testid="merch-save" onClick={save} className="flex-1 py-3 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase text-sm">Save</button>
          <button onClick={onClose} className="px-5 py-3 rounded-full border border-emerald-700/50 text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function OwnerLogin({ login, navigate }) {
  const { loginWithPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await loginWithPassword(email, password);
      if (!u.is_admin) setErr("This account is not the Sir. King Kitchen owner.");
    } catch (e2) {
      setErr(formatApiError(e2?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#0A2818] tropical-grain px-5">
      <div className="rounded-3xl bg-[#181D1A] border border-emerald-900/60 p-8 sm:p-10 max-w-sm w-full text-center">
        <span className="grid place-items-center w-16 h-16 rounded-full bg-white overflow-hidden ring-2 ring-amber-400/60 mx-auto"><img src="https://customer-assets-7cd3h4nn.emergentagent.net/job_caribbean-kitchen-11/artifacts/vjjzbk63_IMG_1456.webp" alt="Sir. King Kitchen logo" className="w-full h-full object-contain" /></span>
        <h1 className="font-display font-black text-2xl uppercase mt-4">Owner Login</h1>
        <p className="text-[#FDFBF7]/60 text-sm mt-2">Sign in to manage Sir. King Kitchen.</p>

        <form onSubmit={submit} className="mt-6 space-y-3 text-left">
          <input data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inp} />
          <input data-testid="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inp} />
          {err && <p data-testid="admin-login-error" className="text-red-400 text-sm text-center">{err}</p>}
          <button data-testid="admin-login-submit" disabled={busy} className="w-full py-3.5 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase tracking-wide flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-5">
          <span className="h-px flex-1 bg-emerald-800/50" />
          <span className="text-xs uppercase tracking-wider text-[#FDFBF7]/40">or</span>
          <span className="h-px flex-1 bg-emerald-800/50" />
        </div>

        <button data-testid="admin-google-login" onClick={login} className="w-full py-3.5 rounded-full border border-emerald-700/50 text-amber-300 font-display font-bold uppercase tracking-wide flex items-center justify-center gap-2 hover:bg-emerald-900/50 transition-colors">
          <i className="fa-brands fa-google" /> Sign in with Google
        </button>
        <button onClick={() => navigate("/")} className="mt-3 text-sm text-[#FDFBF7]/50 hover:text-amber-300">← Back to site</button>
      </div>
    </div>
  );
}

const FOOD_STAGES = ["Order Received", "Order Confirmed", "In the Kitchen", "Ready for Pickup", "Out for Delivery", "Completed", "Cancelled"];
const FOOD_PICKUP_FLOW = ["Order Received", "Order Confirmed", "In the Kitchen", "Ready for Pickup", "Completed"];
const FOOD_DELIVERY_FLOW = ["Order Received", "Order Confirmed", "In the Kitchen", "Out for Delivery", "Completed"];
const CATERING_STAGES = ["New", "Contacted", "Quoted", "Booked", "Closed"];
const MERCH_STAGES = ["Awaiting Payment", "Paid", "Processing", "In Production", "Shipped", "Delivered", "Cancelled", "Refunded"];
const STAGE_COLORS = {
  "Order Received": "border-amber-500/50", "Order Confirmed": "border-amber-400/60", "In the Kitchen": "border-orange-500/60",
  "Ready for Pickup": "border-emerald-500/60", "Out for Delivery": "border-sky-500/60", "Completed": "border-emerald-600/50",
  "Cancelled": "border-red-500/50", "Refunded": "border-red-500/50",
  "New": "border-amber-500/50", "Contacted": "border-sky-500/60", "Quoted": "border-orange-500/60", "Booked": "border-emerald-500/60", "Closed": "border-[#FDFBF7]/20",
  "Awaiting Payment": "border-amber-500/50", "Paid": "border-emerald-500/60", "Processing": "border-orange-500/60", "In Production": "border-orange-400/60", "Shipped": "border-sky-500/60", "Delivered": "border-emerald-600/50",
};
const todayStr = () => new Date().toISOString().slice(0, 10);
const timeInStage = (iso) => {
  if (!iso) return null;
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (isNaN(mins) || mins < 0) return null;
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m in stage`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m in stage`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h in stage`;
};

const PAYOUT_TONE = { paid: "bg-emerald-100 text-emerald-700", in_transit: "bg-amber-100 text-amber-700", pending: "bg-amber-100 text-amber-700", failed: "bg-red-100 text-red-600", canceled: "bg-slate-100 text-slate-500" };

function PayoutsCard({ orders }) {
  const { user } = useAuth();
  if (!user?.is_admin) return null;
  return (
    <>
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <SalesTodayCard orders={orders} />
        <PayoutsPanel />
      </div>
      <WeeklySalesCard orders={orders} />
    </>
  );
}

const lastSevenDays = (orders) => {
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - i);
    days.push({ key: d.toDateString(), label: d.toLocaleDateString(undefined, { weekday: "short" }), date: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }), total: 0, count: 0, isToday: i === 0 });
  }
  const byKey = Object.fromEntries(days.map((d) => [d.key, d]));
  orders.forEach((o) => {
    if (o.payment_status !== "paid" || !o.created_at) return;
    const d = byKey[new Date(o.created_at).toDateString()];
    if (d) { d.total += Number(o.total || 0); d.count += 1; }
  });
  return days;
};

function WeeklySalesCard({ orders }) {
  const days = lastSevenDays(orders);
  const max = Math.max(...days.map((d) => d.total), 0);
  const week = days.reduce((a, d) => a + d.total, 0);
  const best = max > 0 ? days.find((d) => d.total === max) : null;
  return (
    <div data-testid="dashboard-weekly-sales" className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm mt-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <h3 className="font-display font-black text-lg text-slate-900">Last 7 days</h3>
        <div className="text-sm text-slate-500">
          <span data-testid="weekly-sales-total" className="font-bold text-slate-900 tabular-nums">{money(week)}</span> paid
          {best && <> · busiest <span data-testid="weekly-sales-best" className="font-bold text-emerald-700">{best.label} {best.date}</span></>}
        </div>
      </div>
      <div className="flex items-end gap-2 sm:gap-4 h-44">
        {days.map((d, i) => (
          <div key={d.key} data-testid={`weekly-bar-${i}`} className="flex-1 h-full flex flex-col items-center justify-end gap-1 group" title={`${d.label} ${d.date}: ${money(d.total)} · ${d.count} orders`}>
            <span className="text-[10px] sm:text-xs font-bold text-slate-600 tabular-nums">{d.total ? `$${Math.round(d.total)}` : ""}</span>
            <div className={`w-full max-w-[56px] rounded-t-lg transition-[height,background-color] duration-500 ${d === best ? "bg-emerald-500" : d.isToday ? "bg-amber-400" : "bg-slate-300 group-hover:bg-slate-400"}`}
              style={{ height: max ? `${Math.max((d.total / max) * 100, d.total ? 4 : 1)}%` : "1%" }} />
            <span className={`text-[11px] sm:text-xs ${d.isToday ? "font-bold text-slate-900" : "text-slate-500"}`}>{d.isToday ? "Today" : d.label}</span>
          </div>
        ))}
      </div>
      {!best && <p data-testid="weekly-sales-empty" className="text-sm text-slate-400 text-center mt-3">No paid orders in the last 7 days yet.</p>}
    </div>
  );
}

function SalesTodayCard({ orders }) {
  const today = new Date().toDateString();
  const paid = orders.filter((o) => o.payment_status === "paid" && o.created_at && new Date(o.created_at).toDateString() === today);
  const total = paid.reduce((a, o) => a + Number(o.total || 0), 0);
  const food = paid.filter((o) => o.order_kind !== "merch").length;
  const avg = paid.length ? total / paid.length : 0;
  return (
    <div data-testid="dashboard-sales-today" className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm">
      <h3 className="font-display font-black text-lg text-slate-900 mb-3">Today's sales</h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-emerald-50 p-3"><div className="text-xs text-slate-500">Paid sales</div><div data-testid="sales-today-total" className="font-display font-black text-2xl text-emerald-700 tabular-nums">{money(total)}</div></div>
        <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-500">Average order</div><div data-testid="sales-today-avg" className="font-display font-black text-2xl text-slate-800 tabular-nums">{money(avg)}</div></div>
      </div>
      <p data-testid="sales-today-count" className="mt-3 text-sm text-slate-500">
        {paid.length ? `${paid.length} paid order${paid.length === 1 ? "" : "s"} today (${food} food, ${paid.length - food} merch)` : "No paid orders yet today."}
      </p>
      <p className="mt-1 text-xs text-slate-400">Refunded and unpaid orders are not counted.</p>
    </div>
  );
}

function PayoutsPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/stripe/payouts")
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Could not load payouts"));
  }, []);

  const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

  return (
    <div data-testid="dashboard-payouts" className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="font-display font-black text-lg text-slate-900">Stripe payouts</h3>
        {data?.mode === "test" && <span data-testid="payouts-test-mode" className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Test mode</span>}
      </div>
      {error && <p data-testid="payouts-error" className="text-sm text-red-600">{error}</p>}
      {!data && !error && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
      {data && (<>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-emerald-50 p-3"><div className="text-xs text-slate-500">Available</div><div data-testid="payouts-available" className="font-display font-black text-2xl text-emerald-700 tabular-nums">{money(data.available)}</div></div>
          <div className="rounded-xl bg-amber-50 p-3"><div className="text-xs text-slate-500">On the way (pending)</div><div data-testid="payouts-pending" className="font-display font-black text-2xl text-amber-700 tabular-nums">{money(data.pending)}</div></div>
        </div>
        <div className="mt-4 text-sm font-bold text-slate-700">Recent bank deposits</div>
        {data.payouts.length === 0 && <p data-testid="payouts-empty" className="text-slate-400 text-sm py-4 text-center">No payouts yet.</p>}
        <div className="divide-y divide-slate-100">
          {data.payouts.map((p) => (
            <div key={p.id} data-testid={`payout-row-${p.id}`} className="py-2.5 flex items-center justify-between gap-3 text-sm">
              <span className="text-slate-500">{fmtDate(p.arrival_date)}</span>
              <span className="font-bold text-slate-900 tabular-nums">{money(p.amount)}</span>
              <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full capitalize ${PAYOUT_TONE[p.status] || "bg-slate-100 text-slate-500"}`}>{p.status.replace("_", " ")}</span>
            </div>
          ))}
        </div>
      </>)}
    </div>
  );
}

function DashboardTab() {
  const [food, setFood] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [catering, setCatering] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [o, c] = await Promise.all([api.get("/admin/orders"), api.get("/admin/catering")]);
        setAllOrders(o.data);
        setFood(o.data.filter((x) => x.order_kind !== "merch"));
        setCatering(c.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto mt-10" />;

  const hr = new Date().getHours();
  const greet = hr < 12 ? "Good morning" : hr < 18 ? "Good afternoon" : "Good evening";
  const today = todayStr();
  const dayOf = (o) => (o.scheduled_time || o.created_at || "").slice(0, 10);
  const todays = food.filter((o) => dayOf(o) === today).sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
  const PENDING_STAGES = ["Order Received", "Order Confirmed", "In the Kitchen"];
  const OVERDUE_MIN = 30;
  const active = todays.filter((o) => !["Completed", "Cancelled"].includes(o.status));
  const pending = todays.filter((o) => PENDING_STAGES.includes(o.status));
  const readyNow = todays.filter((o) => ["Ready for Pickup", "Out for Delivery"].includes(o.status)).length;
  const toQuote = catering.filter((c) => (c.status || "New") === "New").length;
  const openCatering = catering.filter((c) => c.status !== "Closed").length;

  const minsSince = (iso) => {
    if (!iso) return 0;
    const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    return isNaN(m) || m < 0 ? 0 : m;
  };
  const lateMins = (o) => (PENDING_STAGES.includes(o.status) ? minsSince(o.stage_changed_at || o.created_at) : 0);
  const isOverdue = (o) => lateMins(o) >= OVERDUE_MIN;
  const overdueCount = pending.filter(isOverdue).length;

  const prepMap = new Map();
  pending.forEach((o) => (o.items || []).forEach((i) => { if (i.name) prepMap.set(i.name, (prepMap.get(i.name) || 0) + (i.quantity || 1)); }));
  const prepList = [...prepMap.entries()].map(([name, qty]) => ({ name, qty })).sort((a, b) => b.qty - a.qty);
  const prepTotal = prepList.reduce((s, p) => s + p.qty, 0);
  const prep = new Set(prepList.map((p) => p.name));

  const badge = (status) => {
    if (["Ready for Pickup", "Out for Delivery"].includes(status)) return { label: "Ready", cls: "bg-emerald-100 text-emerald-700" };
    if (["Order Confirmed", "In the Kitchen"].includes(status)) return { label: "Preparing", cls: "bg-emerald-100 text-emerald-700" };
    if (status === "Completed") return { label: "Done", cls: "bg-slate-100 text-slate-500" };
    if (status === "Cancelled") return { label: "Cancelled", cls: "bg-red-100 text-red-600" };
    return { label: "New", cls: "bg-amber-100 text-amber-700" };
  };

  const Stat = ({ label, value, unit, tone, icon: Icon, testid }) => (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-slate-500 text-sm">{label}</span>
        <Icon className="w-4 h-4 text-slate-300" />
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-display font-black text-3xl text-slate-900 tabular-nums">{value}</span>
        <span className={`text-sm font-semibold ${tone}`}>{unit}</span>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-dashboard" className="bg-[#F6F7F6] text-slate-900 rounded-3xl p-4 sm:p-7">
      <h2 data-testid="dashboard-greeting" className="font-display font-black text-2xl sm:text-3xl tracking-tight">{greet}, Sir. King Kitchen</h2>
      <p className="text-slate-500 mt-1 text-sm">{todays.length} order{todays.length === 1 ? "" : "s"} today · {openCatering} catering request{openCatering === 1 ? "" : "s"}</p>

      {overdueCount > 0 && (
        <div data-testid="dashboard-overdue-banner" className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 mt-4 text-sm font-semibold">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
          {overdueCount} order{overdueCount === 1 ? "" : "s"} waiting over {OVERDUE_MIN} min — needs attention
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-5">
        <Stat testid="stat-orders-today" label="Orders today" value={todays.length} unit="pickup & delivery" tone="text-emerald-600" icon={ShoppingBag} />
        <Stat testid="stat-ready-now" label="Ready now" value={readyNow} unit="orders" tone="text-emerald-600" icon={Flame} />
        <Stat testid="stat-catering" label="Catering requests" value={toQuote} unit="to quote" tone="text-orange-600" icon={Sparkles} />
        <Stat testid="stat-prep" label="Prep items" value={prep.size} unit="dishes" tone="text-slate-500" icon={Utensils} />
      </div>

      <PayoutsCard orders={allOrders} />

      <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm mt-4" data-testid="today-orders-list">
        <div className="flex items-baseline gap-2 mb-3">
          <h3 className="font-display font-black text-lg text-slate-900">Today's orders</h3>
          <span className="text-slate-400 text-sm">· Pickup and delivery</span>
        </div>
        {todays.length === 0 && <p className="text-slate-400 text-sm py-6 text-center">No orders scheduled for today yet.</p>}
        <div className="divide-y divide-slate-100">
          {todays.slice(0, 10).map((o) => {
            const b = badge(o.status);
            const late = lateMins(o);
            const overdue = late >= OVERDUE_MIN;
            return (
              <div key={o.order_id} data-testid={`dashboard-order-${o.order_number}`} className={`py-3 px-2 -mx-2 rounded-lg flex items-start justify-between gap-3 ${overdue ? "bg-red-50" : ""}`}>
                <div className="min-w-0">
                  <div className={`font-bold ${overdue ? "text-red-700" : "text-slate-900"}`}>{o.customer_name}</div>
                  <div className="text-sm text-slate-500 truncate">{(o.items || []).map((i) => `${i.quantity > 1 ? i.quantity + "× " : ""}${i.name}`).join(", ")}</div>
                  <div className="text-sm text-slate-400 mt-0.5 capitalize">{o.fulfillment_type} · {(o.scheduled_time || "").slice(11, 16) || "—"}</div>
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${b.cls}`}>{b.label}</span>
                  {overdue && <span data-testid={`dashboard-order-late-${o.order_number}`} className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-red-100 text-red-600">{late}m late</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {prepList.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm mt-4" data-testid="dashboard-prep-list">
          <div className="flex items-baseline gap-2 mb-3">
            <h3 className="font-display font-black text-lg text-slate-900">Prep list</h3>
            <span className="text-slate-400 text-sm">· {prepTotal} dish{prepTotal === 1 ? "" : "es"} across {pending.length} order{pending.length === 1 ? "" : "s"} in prep</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
            {prepList.map((p) => (
              <div key={p.name} data-testid={`prep-${p.name.replace(/\s+/g, "-").toLowerCase()}`} className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-slate-700">{p.name}</span>
                <span className="font-display font-black text-slate-900 tabular-nums">{p.qty}×</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {toQuote > 0 && (
        <div data-testid="dashboard-catering-note" className="bg-sky-50 border border-sky-100 text-sky-800 rounded-2xl px-4 py-3 mt-4 text-sm font-medium">
          {toQuote} catering request{toQuote === 1 ? "" : "s"} need{toQuote === 1 ? "s" : ""} a quote.
        </div>
      )}
    </div>
  );
}

function TrackerTab() {
  const [food, setFood] = useState([]);
  const [catering, setCatering] = useState([]);
  const [merch, setMerch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pipeline, setPipeline] = useState("food");
  const [q, setQ] = useState("");
  const [todayOnly, setTodayOnly] = useState(false);
  const [dragItem, setDragItem] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [newIds, setNewIds] = useState(() => new Set());
  const knownIds = useRef(null);
  const audioRef = useRef(null);
  const soundRef = useRef(true);
  useEffect(() => { soundRef.current = soundOn; }, [soundOn]);

  const playChime = useCallback(() => {
    try {
      if (!audioRef.current) audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const t0 = ctx.currentTime;
      [[880, 0], [1318.5, 0.14]].forEach(([freq, dt]) => {
        const osc = ctx.createOscillator(); const gain = ctx.createGain();
        osc.type = "sine"; osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, t0 + dt);
        gain.gain.exponentialRampToValueAtTime(0.3, t0 + dt + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dt + 0.4);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t0 + dt); osc.stop(t0 + dt + 0.45);
      });
    } catch (e) { console.warn("order chime unavailable", e); }
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [o, c, m] = await Promise.all([
        api.get("/admin/orders"), api.get("/admin/catering"), api.get("/admin/merch/orders"),
      ]);
      const foodList = o.data.filter((x) => x.order_kind !== "merch");
      setFood(foodList); setCatering(c.data); setMerch(m.data);
      const current = [
        ...foodList.map((x) => ({ id: x.order_id, label: `New ${x.fulfillment_type} order #${x.order_number} · ${money(x.total)}` })),
        ...m.data.map((x) => ({ id: x.order_id, label: `New merch order #${x.order_number} · ${money(x.total)}` })),
        ...c.data.map((x) => ({ id: x.inquiry_id, label: `New catering request — ${x.name}` })),
      ];
      if (knownIds.current === null) {
        knownIds.current = new Set(current.map((x) => x.id));
      } else {
        const fresh = current.filter((x) => !knownIds.current.has(x.id));
        if (fresh.length) {
          if (soundRef.current) playChime();
          if (fresh.length <= 2) fresh.forEach((f) => toast.success(f.label, { duration: 8000 }));
          else toast.success(`${fresh.length} new orders came in`, { duration: 8000 });
          setNewIds((prev) => { const n = new Set(prev); fresh.forEach((f) => n.add(f.id)); return n; });
          fresh.forEach((f) => knownIds.current.add(f.id));
          setTimeout(() => setNewIds((prev) => { const n = new Set(prev); fresh.forEach((f) => n.delete(f.id)); return n; }), 12000);
        }
      }
    } finally { setLoading(false); }
  }, [playChime]);
  useEffect(() => {
    loadAll();
    const iv = setInterval(loadAll, 20000);
    return () => clearInterval(iv);
  }, [loadAll]);

  const advance = async (type, item) => {
    try {
      if (type === "food") {
        const flow = item.fulfillment_type === "delivery" ? FOOD_DELIVERY_FLOW : FOOD_PICKUP_FLOW;
        const next = flow[flow.indexOf(item.status) + 1];
        if (!next) return;
        await api.put(`/admin/orders/${item.order_id}/status`, { status: next });
      } else if (type === "catering") {
        const next = CATERING_STAGES[CATERING_STAGES.indexOf(item.status) + 1];
        if (!next) return;
        await api.put(`/admin/catering/${item.inquiry_id}/status`, { status: next });
      } else {
        const cur = item.fulfillment_status || "Awaiting Payment";
        const next = MERCH_STAGES.slice(0, 6)[MERCH_STAGES.indexOf(cur) + 1];
        if (!next) return;
        await api.put(`/admin/merch/orders/${item.order_id}/status`, { fulfillment_status: next });
      }
      toast.success("Moved to next stage");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update stage"); }
  };

  const jump = async (type, item, stage) => {
    try {
      if (type === "food") await api.put(`/admin/orders/${item.order_id}/status`, { status: stage });
      else if (type === "catering") await api.put(`/admin/catering/${item.inquiry_id}/status`, { status: stage });
      else await api.put(`/admin/merch/orders/${item.order_id}/status`, { fulfillment_status: stage });
      toast.success(`Moved to ${stage}`);
      loadAll();
    } catch (e) { toast.error("Could not update stage"); }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto mt-10" />;

  const ql = q.trim().toLowerCase();
  const matchQ = (fields) => !ql || fields.filter(Boolean).some((f) => String(f).toLowerCase().includes(ql));

  const cfg = {
    food: {
      stages: FOOD_STAGES, items: food, stageOf: (x) => x.status,
      filter: (x) => matchQ([x.customer_name, x.customer_phone, x.order_number]) && (!todayOnly || (x.scheduled_time || "").slice(0, 10) === todayStr()),
    },
    catering: {
      stages: CATERING_STAGES, items: catering, stageOf: (x) => x.status || "New",
      filter: (x) => matchQ([x.name, x.phone, x.email]) && (!todayOnly || (x.event_date || "").slice(0, 10) === todayStr()),
    },
    merch: {
      stages: MERCH_STAGES, items: merch, stageOf: (x) => x.fulfillment_status || "Awaiting Payment",
      filter: (x) => matchQ([x.customer_name, x.customer_email, x.order_number]) && (!todayOnly || (x.created_at || "").slice(0, 10) === todayStr()),
    },
  }[pipeline];

  const visible = cfg.items.filter(cfg.filter);
  const grouped = cfg.stages.map((s) => ({ stage: s, cards: visible.filter((x) => cfg.stageOf(x) === s) }));
  const tabs = [
    { id: "food", label: "Food Orders", icon: ShoppingBag, n: food.length },
    { id: "catering", label: "Catering", icon: Sparkles, n: catering.length },
    { id: "merch", label: "Merch", icon: Shirt, n: merch.length },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`tracker-pipeline-${t.id}`} onClick={() => setPipeline(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-display font-bold uppercase tracking-wide transition-colors ${pipeline === t.id ? "bg-amber-400 text-[#0A2818]" : "border border-emerald-800/50 text-[#FDFBF7]/80 hover:border-amber-500/50"}`}>
            <t.icon className="w-4 h-4" /> {t.label} <span className={`text-xs px-1.5 py-0.5 rounded-full ${pipeline === t.id ? "bg-[#0A2818]/20" : "bg-emerald-900/60"}`}>{t.n}</span>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#FDFBF7]/40" />
          <input data-testid="tracker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone or order #"
            className={`${inp} pl-9`} />
        </div>
        <button data-testid="tracker-today-toggle" onClick={() => setTodayOnly((v) => !v)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold transition-colors ${todayOnly ? "bg-emerald-600 text-[#FDFBF7]" : "border border-emerald-800/50 text-[#FDFBF7]/80"}`}>
          <Clock className="w-4 h-4" /> Today
        </button>
        <button data-testid="tracker-sound-toggle" onClick={() => { setSoundOn((v) => !v); if (!audioRef.current) { try { audioRef.current = new (window.AudioContext || window.webkitAudioContext)(); audioRef.current.resume(); } catch (e) { console.warn("audio unavailable", e); } } }}
          title={soundOn ? "New-order sound on" : "New-order sound off"}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold transition-colors ${soundOn ? "bg-amber-400 text-[#0A2818]" : "border border-emerald-800/50 text-[#FDFBF7]/60"}`}>
          {soundOn ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />} Chime
        </button>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4 -mx-1 px-1">
        {grouped.map(({ stage, cards }) => (
          <div key={stage} data-testid={`tracker-col-${stage.replace(/\s+/g, "-").toLowerCase()}`}
            onDragOver={(e) => { if (dragItem) { e.preventDefault(); setDragOverStage(stage); } }}
            onDragLeave={() => setDragOverStage((s) => (s === stage ? null : s))}
            onDrop={(e) => { e.preventDefault(); setDragOverStage(null); if (dragItem && cfg.stageOf(dragItem) !== stage) jump(pipeline, dragItem, stage); setDragItem(null); }}
            className={`shrink-0 w-[300px] rounded-xl transition-colors ${dragOverStage === stage ? "bg-amber-400/10 ring-2 ring-amber-400/50" : ""}`}>
            <div className={`flex items-center justify-between px-3 py-2 rounded-t-xl bg-[#181D1A] border-l-4 ${STAGE_COLORS[stage] || "border-emerald-700/50"}`}>
              <span className="font-display font-black uppercase text-sm tracking-wide">{stage}</span>
              <span className="text-xs font-bold bg-emerald-900/60 rounded-full px-2 py-0.5">{cards.length}</span>
            </div>
            <div className="space-y-2 mt-2 min-h-[60px] px-1">
              {cards.length === 0 && <div className="text-xs text-[#FDFBF7]/30 text-center py-6 border border-dashed border-emerald-900/40 rounded-xl">Drop here</div>}
              {cards.map((item) => (
                <TrackerCard key={item.order_id || item.inquiry_id} type={pipeline} item={item} stages={cfg.stages}
                  isNew={newIds.has(item.order_id || item.inquiry_id)}
                  onAdvance={() => advance(pipeline, item)} onJump={(s) => jump(pipeline, item, s)}
                  onDragStart={() => setDragItem(item)} onDragEnd={() => { setDragItem(null); setDragOverStage(null); }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrackerCard({ type, item, stages, onAdvance, onJump, onDragStart, onDragEnd, isNew }) {
  const stage = type === "merch" ? (item.fulfillment_status || "Awaiting Payment") : (item.status || "New");
  const terminal = ["Completed", "Cancelled", "Closed", "Delivered", "Refunded"].includes(stage);
  const num = item.order_number ? `#${item.order_number}` : "";
  const inStage = timeInStage(item.stage_changed_at || item.updated_at || item.created_at);
  const displayStages = type === "food"
    ? stages.filter((s) => !(item.fulfillment_type === "delivery" && s === "Ready for Pickup") && !(item.fulfillment_type !== "delivery" && s === "Out for Delivery"))
    : stages;
  return (
    <div draggable onDragStart={onDragStart} onDragEnd={onDragEnd}
      className={`bg-[#101311] border rounded-xl p-3 text-sm cursor-grab active:cursor-grabbing transition-colors ${isNew ? "border-amber-400 ring-2 ring-amber-400/60 animate-pulse" : "border-emerald-900/60 hover:border-amber-500/40"}`}>
      {type === "food" && (
        <>
          <div className="flex items-center justify-between gap-2">
            <span className="font-display font-black">{num} · {money(item.total)}</span>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full flex items-center gap-1 ${item.fulfillment_type === "delivery" ? "bg-sky-900/50 text-sky-300" : "bg-emerald-900/60 text-emerald-300"}`}>
              {item.fulfillment_type === "delivery" ? <Truck className="w-3 h-3" /> : <Store className="w-3 h-3" />}{item.fulfillment_type}
            </span>
          </div>
          <div className="text-[#FDFBF7]/70 mt-1">{item.customer_name} · {item.customer_phone}</div>
          <div className="text-xs text-[#FDFBF7]/45">{item.scheduled_time} · <span className={item.payment_status === "paid" ? "text-emerald-400" : item.payment_status === "refunded" ? "text-red-300" : ""}>{item.payment_status}</span></div>
          <div className="text-xs text-[#FDFBF7]/60 mt-1 line-clamp-2">{(item.items || []).map((i) => `${i.quantity}× ${i.name}`).join(", ")}</div>
        </>
      )}
      {type === "catering" && (
        <>
          <div className="font-display font-black">{item.name} · {item.guests || "?"} guests</div>
          <div className="text-[#FDFBF7]/70 mt-1">{item.phone}</div>
          <div className="text-xs text-[#FDFBF7]/45">{item.event_type || "—"} · {item.event_date || "no date"}</div>
          <div className="text-xs text-amber-300/80">{item.service_type || ""}{item.budget ? ` · ${item.budget}` : ""}</div>
        </>
      )}
      {type === "merch" && (
        <>
          <div className="flex items-center justify-between gap-2">
            <span className="font-display font-black">{num} · {money(item.total)}</span>
            <Package className="w-3.5 h-3.5 text-[#FDFBF7]/40" />
          </div>
          <div className="text-[#FDFBF7]/70 mt-1">{item.customer_name}</div>
          <div className="text-xs text-[#FDFBF7]/45"><span className={item.payment_status === "paid" ? "text-emerald-400" : ""}>{item.payment_status}</span>{item.tracking_number ? ` · ${item.tracking_number}` : ""}</div>
        </>
      )}
      {inStage && !terminal && (
        <div className="mt-3 -mb-1">
          <span className="flex items-center gap-1 text-[10px] text-[#FDFBF7]/40" data-testid={`tracker-time-${item.order_number || item.inquiry_id}`}><Clock className="w-3 h-3" /> {inStage}</span>
        </div>
      )}
      <div className="flex items-center gap-2 mt-3">
        <select data-testid={`tracker-stage-${item.order_number || item.inquiry_id}`} value={stage} onChange={(e) => onJump(e.target.value)}
          className="flex-1 bg-[#0A0D0B] rounded-lg px-2 py-1.5 text-xs border border-emerald-800/50 outline-none focus:border-amber-400">
          {displayStages.map((s) => <option key={s}>{s}</option>)}
        </select>
        {!terminal && (
          <button data-testid={`tracker-advance-${item.order_number || item.inquiry_id}`} onClick={onAdvance}
            className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-400 text-[#0A2818] font-bold text-xs uppercase active:scale-95 transition-transform">
            Next <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => { try { const r = await api.get("/admin/orders"); setOrders(r.data); } finally { setLoading(false); } }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}/status`, { status });
    toast.success(`Order marked ${status}`);
    load();
  };

  const cancelOrder = async (o) => {
    const paid = o.payment_status === "paid";
    const ok = window.confirm(paid
      ? `Refund ${money(o.total)} to the customer and cancel order #${o.order_number}? This cannot be undone.`
      : `Cancel order #${o.order_number}?`);
    if (!ok) return;
    try {
      const { data } = await api.post(`/admin/orders/${o.order_id}/cancel`);
      toast.success(data.refund ? `Refunded ${money(data.refund.amount)} & cancelled` : "Order cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel this order");
    }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto mt-10" />;
  if (!orders.length) return <Card><p className="text-[#FDFBF7]/60 text-center py-6">No orders yet.</p></Card>;

  return (
    <div className="space-y-3">
      {orders.map((o) => (
        <Card key={o.order_id}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-display font-black text-lg">#{o.order_number} · {money(o.total)}</div>
              <div className="text-sm text-[#FDFBF7]/60">{o.customer_name} · {o.customer_phone} · {o.fulfillment_type}</div>
              {o.address && <div className="text-xs text-[#FDFBF7]/50">{o.address} {o.zip_code}</div>}
              <div className="text-xs text-[#FDFBF7]/50 mt-1">{o.scheduled_time} · <span className={o.payment_status === "refunded" ? "text-red-300 font-bold" : o.payment_status === "paid" ? "text-emerald-400 font-bold" : ""}>{o.payment_status}</span></div>
              <div className="text-sm text-[#FDFBF7]/70 mt-2">{o.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <select data-testid={`admin-order-status-${o.order_number}`} value={o.status} onChange={(e) => setStatus(o.order_id, e.target.value)} className={`${inp} max-w-[220px]`}>
                {ORDER_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
              {o.status !== "Cancelled" && (
                <button data-testid={`admin-order-cancel-${o.order_number}`} onClick={() => cancelOrder(o)} className="text-xs font-bold uppercase tracking-wide text-red-300 hover:text-red-200 border border-red-500/40 hover:border-red-400 rounded-full px-4 py-2 transition-colors">
                  {o.payment_status === "paid" ? "Refund & Cancel" : "Cancel Order"}
                </button>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function CateringTab() {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => { const r = await api.get("/admin/catering"); setItems(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => { await api.put(`/admin/catering/${id}/status`, { status }); toast.success("Updated"); load(); };
  if (!items.length) return <Card><p className="text-[#FDFBF7]/60 text-center py-6">No catering inquiries yet.</p></Card>;
  return (
    <div className="space-y-3">
      {items.map((c) => (
        <Card key={c.inquiry_id}>
          <div className="flex flex-wrap justify-between gap-3">
            <div>
              <div className="font-display font-black text-lg">{c.name} · {c.guests || "?"} guests</div>
              <div className="text-sm text-[#FDFBF7]/60">{c.email} · {c.phone}</div>
              <div className="text-sm text-[#FDFBF7]/70 mt-1">{c.event_type} · {c.event_date} · {c.location}</div>
              <div className="text-sm text-[#FDFBF7]/70">Service: {c.service_type} · Budget: {c.budget || "—"}</div>
              {c.preferred_menu && <div className="text-sm text-amber-300 mt-1">Menu: {c.preferred_menu}</div>}
              {c.details && <div className="text-sm text-[#FDFBF7]/60 mt-1 italic">{c.details}</div>}
            </div>
            <select value={c.status} onChange={(e) => setStatus(c.inquiry_id, e.target.value)} className={`${inp} max-w-[180px]`}>
              {["New", "Contacted", "Quoted", "Booked", "Closed"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </Card>
      ))}
    </div>
  );
}

function MenuTab() {
  const { reload: reloadPublic } = useData();
  const [menu, setMenu] = useState({ categories: [], items: [] });
  const [editing, setEditing] = useState(null);
  const reload = useCallback(async () => {
    try {
      const r = await api.get("/admin/menu");
      setMenu(r.data);
    } catch {
      toast.error("Could not load menu");
    }
    reloadPublic();
  }, [reloadPublic]);
  useEffect(() => { reload(); }, [reload]);

  const toggleSold = async (item) => { await api.put(`/admin/menu-items/${item.item_id}/sold-out`, { sold_out: !item.sold_out }); await reload(); toast.success(item.sold_out ? "Back in stock" : "Marked sold out"); };
  const toggleHidden = async (item) => { await api.put(`/admin/menu-items/${item.item_id}/hidden`, { hidden: !item.hidden }); await reload(); toast.success(item.hidden ? `${item.name} is visible on the menu` : `${item.name} is hidden from customers`); };
  const del = async (item) => { if (!window.confirm(`Delete ${item.name}?`)) return; await api.delete(`/admin/menu-items/${item.item_id}`); await reload(); toast.success("Deleted"); };
  const blank = () => ({ name: "", description: "", price: 0, category_id: menu.categories[0]?.category_id, image: "", spice: false, signature: false, tags: [], addons: [], options: [], flavors: [], rice_options: [], side_options: [], sides_count: 0, order: 99 });

  return (
    <div>
      <button data-testid="admin-add-item" onClick={() => setEditing(blank())} className="mb-4 flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase text-sm"><Plus className="w-4 h-4" /> Add Item</button>
      <div className="grid sm:grid-cols-2 gap-3">
        {menu.items.map((i) => (
          <Card key={i.item_id} className={`flex gap-3 ${i.hidden ? "opacity-60" : ""}`} data-testid={`admin-menu-item-${i.item_id}`}>
            <img src={i.image} alt={i.name} className={`w-16 h-16 rounded-lg object-cover ${i.hidden ? "grayscale" : ""}`} />
            <div className="flex-1 min-w-0">
              <div className="flex justify-between gap-2"><span className="font-display font-bold truncate">{i.name}</span><span className="text-amber-400 font-bold">{money(i.price)}</span></div>
              <div className="text-xs text-[#FDFBF7]/50">{menu.categories.find((c) => c.category_id === i.category_id)?.name}{i.sold_out ? " · SOLD OUT" : ""}{i.hidden && <span data-testid={`admin-hidden-badge-${i.item_id}`} className="text-red-300 font-bold"> · HIDDEN</span>}</div>
              <div className="flex gap-2 mt-2">
                <button data-testid={`admin-edit-${i.item_id}`} onClick={() => setEditing(i)} className="text-xs px-3 py-1.5 rounded-full border border-emerald-700/50">Edit</button>
                <button data-testid={`admin-sold-${i.item_id}`} onClick={() => toggleSold(i)} className="text-xs px-3 py-1.5 rounded-full border border-emerald-700/50">{i.sold_out ? "Restock" : "Sold out"}</button>
                <button data-testid={`admin-hide-${i.item_id}`} onClick={() => toggleHidden(i)} title={i.hidden ? "Show on website" : "Hide from website"} aria-label={i.hidden ? "Show on website" : "Hide from website"} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${i.hidden ? "border-red-700/60 text-red-300 bg-red-900/20" : "border-emerald-700/50 hover:border-amber-400/60"}`}>{i.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</button>
                <button data-testid={`admin-delete-${i.item_id}`} onClick={() => del(i)} className="text-xs px-3 py-1.5 rounded-full border border-red-800/50 text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {editing && <ItemEditor item={editing} categories={menu.categories} onClose={() => setEditing(null)} onSaved={reload} />}
    </div>
  );
}

function ItemEditor({ item, categories, onClose, onSaved }) {
  const [f, setF] = useState({ ...item });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    const payload = { ...f, price: Number(f.price), addons: f.addons || [], tags: f.tags || [],
      flavors: (f.flavors || []).map((x) => String(x).trim()).filter(Boolean),
      rice_options: (f.rice_options || []).map((x) => String(x).trim()).filter(Boolean),
      side_options: (f.side_options || []).map((x) => String(x).trim()).filter(Boolean),
      sides_count: Number(f.sides_count) || 0,
      options: (f.options || []).filter((o) => o.name).map((o) => ({ name: o.name, price: Number(o.price) || 0 })) };
    if (payload.options.length) payload.price = Math.min(...payload.options.map((o) => o.price));
    if (item.item_id) await api.put(`/admin/menu-items/${item.item_id}`, payload);
    else await api.post("/admin/menu-items", payload);
    toast.success("Saved");
    await onSaved();
    onClose();
  };
  const setOpt = (idx, k, v) => setF((p) => ({ ...p, options: p.options.map((o, i) => (i === idx ? { ...o, [k]: v } : o)) }));
  const addOpt = () => setF((p) => ({ ...p, options: [...(p.options || []), { name: "", price: 0 }] }));
  const delOpt = (idx) => setF((p) => ({ ...p, options: p.options.filter((_, i) => i !== idx) }));
  return (
    <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-[#181D1A] border border-emerald-800/60 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto no-scrollbar" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-black text-xl uppercase mb-4">{item.item_id ? "Edit" : "Add"} Item</h3>
        <div className="space-y-3">
          <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Name" className={inp} data-testid="admin-item-name" />
          <textarea value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" rows={2} className={inp} data-testid="admin-item-description" />
          <AiDraftButton kind="dish" testid="admin-item-ai-description" label="Write description with AI" prompt={`Dish: ${f.name}\nCategory: ${categories.find((c) => c.category_id === f.category_id)?.name || ""}\nOwner notes / current text: ${f.description || ""}`} onResult={(d) => set("description", d.text)} />
          <div className="grid grid-cols-2 gap-3">
            <input type="number" step="0.01" value={f.price} onChange={(e) => set("price", e.target.value)} placeholder="Price" className={inp} data-testid="admin-item-price" />
            <select value={f.category_id} onChange={(e) => set("category_id", e.target.value)} className={inp}>{categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}</select>
          </div>
          <ImageUploadField label="Dish Photo" value={f.image} onChange={(v) => set("image", v)} testid="admin-item-image-upload" />
          <div className="rounded-xl border border-emerald-800/50 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-amber-400">Sizes / Options</span>
              <button type="button" data-testid="admin-item-add-option" onClick={addOpt} className="text-amber-400 text-xs font-bold">+ Add option</button>
            </div>
            <p className="text-[11px] text-[#FDFBF7]/40 mb-2">Add two or more for size-based pricing (e.g. Small/Large). Leave empty for a single price.</p>
            {(f.options || []).map((o, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input value={o.name} onChange={(e) => setOpt(i, "name", e.target.value)} placeholder="Option name (e.g. Large)" className={inp} data-testid={`admin-item-option-name-${i}`} />
                <input type="number" step="0.01" value={o.price} onChange={(e) => setOpt(i, "price", e.target.value)} placeholder="Price" className="w-24 rounded-lg bg-[#101311] border border-emerald-800/50 px-3 py-2.5 text-sm outline-none focus:border-amber-400" data-testid={`admin-item-option-price-${i}`} />
                <button type="button" onClick={() => delOpt(i)} className="text-[#FDFBF7]/40 hover:text-red-400 px-2"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <input value={(f.flavors || []).join(", ")} onChange={(e) => set("flavors", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="Flavors (comma-separated, e.g. BBQ, Buffalo, Jerk)" className={inp} data-testid="admin-item-flavors" />
          <div className="rounded-xl border border-emerald-800/50 p-3 space-y-2">
            <span className="text-sm font-bold text-amber-400">Included Sides (for entrées)</span>
            <input value={(f.rice_options || []).join(", ")} onChange={(e) => set("rice_options", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="Rice choices (e.g. Rice & Peas, White Rice)" className={inp} data-testid="admin-item-rice-options" />
            <input value={(f.side_options || []).join(", ")} onChange={(e) => set("side_options", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="Side choices (comma-separated)" className={inp} data-testid="admin-item-side-options" />
            <input type="number" value={f.sides_count || 0} onChange={(e) => set("sides_count", e.target.value)} placeholder="Number of sides customer picks" className={inp} data-testid="admin-item-sides-count" />
          </div>
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.spice} onChange={(e) => set("spice", e.target.checked)} className="accent-amber-400" /> Spicy</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.signature} onChange={(e) => set("signature", e.target.checked)} className="accent-amber-400" /> Signature</label>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 py-3 rounded-full border border-emerald-700/50">Cancel</button>
            <button data-testid="admin-item-save" onClick={save} className="flex-1 py-3 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase flex items-center justify-center gap-2"><Save className="w-4 h-4" /> Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PromotionsTab() {
  const { reload } = useData();
  const [promos, setPromos] = useState([]);
  const [text, setText] = useState("");
  const load = useCallback(async () => { const r = await api.get("/admin/promotions"); setPromos(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!text.trim()) return; await api.post("/admin/promotions", { text, active: true, order: promos.length + 1, style: "gold" }); setText(""); await load(); await reload(); toast.success("Added"); };
  const toggle = async (p) => { await api.put(`/admin/promotions/${p.promo_id}`, { ...p, active: !p.active }); await load(); await reload(); };
  const del = async (p) => { await api.delete(`/admin/promotions/${p.promo_id}`); await load(); await reload(); toast.success("Deleted"); };
  return (
    <div>
      <Card className="flex gap-2 mb-4">
        <input data-testid="admin-promo-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="BUY 3 PLATES — GET FREE DELIVERY" className={inp} />
        <button data-testid="admin-promo-add" onClick={add} className="px-5 rounded-lg bg-amber-400 text-[#0A2818] font-bold">Add</button>
      </Card>
      <div className="space-y-2">
        {promos.map((p) => (
          <Card key={p.promo_id} className="flex items-center justify-between gap-3">
            <span className={`font-display font-bold uppercase ${p.active ? "text-amber-300" : "text-[#FDFBF7]/40 line-through"}`}>{p.text}</span>
            <div className="flex gap-2">
              <button onClick={() => toggle(p)} className="text-xs px-3 py-1.5 rounded-full border border-emerald-700/50">{p.active ? "Disable" : "Enable"}</button>
              <button onClick={() => del(p)} className="text-xs px-3 py-1.5 rounded-full border border-red-800/50 text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function ReviewsTab() {
  const { reload } = useData();
  const [reviews, setReviews] = useState([]);
  const [f, setF] = useState({ name: "", text: "", rating: 5 });
  const load = useCallback(async () => { const r = await api.get("/admin/reviews"); setReviews(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!f.name || !f.text) return toast.error("Name and review required"); await api.post("/admin/reviews", { ...f, rating: Number(f.rating), published: true, status: "approved", order: reviews.length + 1 }); setF({ name: "", text: "", rating: 5 }); await load(); await reload(); toast.success("Added"); };
  const approve = async (r) => { await api.post(`/admin/reviews/${r.review_id}/approve`); await load(); await reload(); toast.success("Approved & live"); };
  const del = async (r) => { await api.delete(`/admin/reviews/${r.review_id}`); await load(); await reload(); toast.success("Removed"); };
  const pending = reviews.filter((r) => !r.published);
  const live = reviews.filter((r) => r.published);
  const Stars = ({ n }) => <span className="text-amber-400">{"★".repeat(n || 5)}</span>;
  return (
    <div className="space-y-5">
      {pending.length > 0 && (
        <Card>
          <div className="flex items-center gap-2 mb-3"><Star className="w-5 h-5 text-amber-400" /><h2 className="font-display font-black text-lg uppercase">Pending Review{pending.length > 1 ? "s" : ""}</h2><span className="text-xs bg-amber-400 text-[#0A2818] font-bold rounded-full px-2 py-0.5">{pending.length}</span></div>
          <div className="space-y-2">
            {pending.map((r) => (
              <div key={r.review_id} data-testid={`pending-review-${r.review_id}`} className="rounded-xl border border-amber-700/30 bg-[#101311] p-3">
                <div className="font-bold">{r.name} · <Stars n={r.rating} /></div>
                <div className="text-sm text-[#FDFBF7]/70 mt-1">{r.text}</div>
                <ReviewReply r={r} onSaved={load} />
                <div className="flex gap-2 mt-3">
                  <button data-testid={`approve-review-${r.review_id}`} onClick={() => approve(r)} className="px-4 py-2 rounded-lg bg-amber-400 text-[#0A2818] font-bold text-sm min-h-[40px]">Approve & Post</button>
                  <button data-testid={`reject-review-${r.review_id}`} onClick={() => del(r)} className="px-4 py-2 rounded-lg border border-red-500/40 text-red-300 font-bold text-sm min-h-[40px]">Reject</button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
      <Card className="space-y-3">
        <div className="text-xs uppercase tracking-wider text-amber-400 font-bold">Add a review yourself</div>
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Customer name" className={inp} data-testid="admin-review-name" />
        <textarea value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} placeholder="Review text (only add real reviews)" rows={2} className={inp} data-testid="admin-review-text" />
        <div className="flex gap-2 items-center">
          <select value={f.rating} onChange={(e) => setF({ ...f, rating: e.target.value })} className={`${inp} max-w-[120px]`}>{[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{`${n} ★`}</option>)}</select>
          <button data-testid="admin-review-add" onClick={add} className="px-5 py-2.5 rounded-lg bg-amber-400 text-[#0A2818] font-bold">Add Review</button>
        </div>
      </Card>
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-[#FDFBF7]/50 font-bold">Live on site ({live.length})</div>
        {live.map((r) => (
          <Card key={r.review_id} className="flex justify-between gap-3" data-testid={`live-review-${r.review_id}`}>
            <div className="flex-1 min-w-0"><div className="font-bold">{r.name} · <Stars n={r.rating} /></div><div className="text-sm text-[#FDFBF7]/70">{r.text}</div><ReviewReply r={r} onSaved={async () => { await load(); await reload(); }} /></div>
            <button onClick={() => del(r)} className="text-red-400 self-start"><Trash2 className="w-4 h-4" /></button>
          </Card>
        ))}
      </div>
    </div>
  );
}

function BusinessHoursTab() {
  const [cfg, setCfg] = useState(null);
  const [cur, setCur] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadCfg = useCallback(async () => {
    const r = await api.get("/admin/business-status");
    setCfg(r.data.config);
    setCur(r.data.current);
  }, []);
  useEffect(() => { loadCfg(); }, [loadCfg]);
  if (!cfg) return <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto mt-10" />;

  const ov = cfg.override || { mode: "auto", until: null, reason: "" };
  const setOv = (patch) => setCfg((p) => ({ ...p, override: { ...ov, ...patch } }));
  const setDay = (i, field, v) => setCfg((p) => ({ ...p, schedule: p.schedule.map((d, idx) => idx === i ? { ...d, [field]: v } : d) }));

  const persist = async (next, msg) => {
    setSaving(true);
    try {
      const body = next || cfg;
      const r = await api.put("/admin/business-status", body);
      setCfg(r.data.config); setCur(r.data.current);
      toast.success(msg || "Business hours saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const setMode = (mode) => { const next = { ...cfg, override: { ...ov, mode, until: mode === "auto" ? null : ov.until } }; setCfg(next); persist(next, mode === "auto" ? "Back to automatic schedule" : `Forced ${mode.toUpperCase()}`); };

  const tempClose = (kind) => {
    let until = null, reason = "Temporarily closed";
    if (kind === "today") { const d = new Date(); d.setHours(23, 59, 0, 0); until = d.toISOString(); reason = "Closed for the rest of today"; }
    if (kind === "manual") { until = null; reason = "Temporarily closed"; }
    const next = { ...cfg, override: { mode: "closed", until, reason } };
    setCfg(next); persist(next, "Temporary closure active");
  };

  const statusColor = cur?.status === "open" ? "text-emerald-400" : cur?.status === "closing_soon" ? "text-amber-400" : "text-red-400";
  const dot = cur?.status === "open" ? "🟢" : cur?.status === "closing_soon" ? "🟡" : "🔴";

  const addSpecial = () => setCfg((p) => ({ ...p, special: [...(p.special || []), { date: "", closed: true, open: "11:00", close: "21:00", note: "" }] }));
  const setSpecial = (i, field, v) => setCfg((p) => ({ ...p, special: p.special.map((s, idx) => idx === i ? { ...s, [field]: v } : s) }));
  const rmSpecial = (i) => setCfg((p) => ({ ...p, special: p.special.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-4">
      <Card className="space-y-3" >
        <h3 className="font-display font-black uppercase text-amber-400">Business Status</h3>
        <div className="rounded-xl bg-[#101311] border border-emerald-800/50 p-4" data-testid="admin-current-status">
          <div className="text-xs uppercase tracking-wider text-[#FDFBF7]/50">Current Customer-Facing Status</div>
          <div className={`font-display font-black text-2xl mt-1 ${statusColor}`}>{dot} {cur?.label}</div>
          <div className="text-sm text-[#FDFBF7]/70 mt-0.5">{cur?.message}</div>
        </div>
        {ov.mode !== "auto" && (
          <div className="rounded-lg bg-amber-950/40 border border-amber-600/50 text-amber-300 text-sm px-4 py-3" data-testid="admin-override-warning">
            ⚠️ Manual override is active ({ov.mode === "open" ? "Force Open" : "Force Closed"}). This overrides your scheduled business hours{ov.until ? " until it expires" : " until you switch back to Automatic"}.
          </div>
        )}
        <div>
          <div className="text-xs uppercase tracking-wider text-amber-400 font-bold mb-2">Status Mode</div>
          <div className="grid grid-cols-3 gap-2">
            {[["auto", "⚙️ Automatic"], ["open", "🟢 Force Open"], ["closed", "🔴 Force Closed"]].map(([m, lbl]) => (
              <button key={m} data-testid={`admin-mode-${m}`} onClick={() => setMode(m)} className={`py-2.5 rounded-full text-sm font-display font-bold uppercase border transition-colors ${ov.mode === m ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-800/50 text-[#FDFBF7]/80"}`}>{lbl}</button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-amber-400 font-bold mb-2">Close Temporarily</div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="admin-close-today" onClick={() => tempClose("today")} className="px-4 py-2 rounded-full border border-red-700/50 text-red-300 text-sm font-semibold">Close rest of today</button>
            <button data-testid="admin-close-manual" onClick={() => tempClose("manual")} className="px-4 py-2 rounded-full border border-red-700/50 text-red-300 text-sm font-semibold">Close until I reopen</button>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-sm text-[#FDFBF7]/60">Close until:</span>
            <input type="datetime-local" data-testid="admin-close-until" onChange={(e) => { if (e.target.value) { const next = { ...cfg, override: { mode: "closed", until: new Date(e.target.value).toISOString(), reason: "Temporarily closed" } }; setCfg(next); persist(next, "Temporary closure set"); } }} className={inp} />
          </div>
        </div>
      </Card>

      <Card className="space-y-3">
        <h3 className="font-display font-black uppercase text-amber-400">Weekly Hours</h3>
        {cfg.schedule.map((d, i) => (
          <div key={d.day} className="flex flex-wrap items-center gap-3" data-testid={`admin-day-${d.day.toLowerCase()}`}>
            <span className="w-24 text-sm font-semibold">{d.day}</span>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!d.closed} onChange={(e) => setDay(i, "closed", !e.target.checked)} className="accent-amber-400" /> Open</label>
            {!d.closed ? (
              <>
                <input type="time" value={d.open || "11:00"} onChange={(e) => setDay(i, "open", e.target.value)} data-testid={`admin-day-open-${d.day.toLowerCase()}`} className="bg-[#101311] rounded-lg px-3 py-2 text-sm border border-emerald-800/50 outline-none focus:border-amber-400" />
                <span className="text-[#FDFBF7]/50">to</span>
                <input type="time" value={d.close || "21:00"} onChange={(e) => setDay(i, "close", e.target.value)} data-testid={`admin-day-close-${d.day.toLowerCase()}`} className="bg-[#101311] rounded-lg px-3 py-2 text-sm border border-emerald-800/50 outline-none focus:border-amber-400" />
              </>
            ) : <span className="text-sm text-red-400 font-semibold">Closed</span>}
          </div>
        ))}
      </Card>

      <Card className="space-y-3">
        <h3 className="font-display font-black uppercase text-amber-400">Special / Holiday Hours</h3>
        {(cfg.special || []).map((s, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2 border-b border-emerald-900/50 pb-2">
            <input type="date" value={s.date || ""} onChange={(e) => setSpecial(i, "date", e.target.value)} className="bg-[#101311] rounded-lg px-3 py-2 text-sm border border-emerald-800/50 outline-none focus:border-amber-400" />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.closed} onChange={(e) => setSpecial(i, "closed", e.target.checked)} className="accent-amber-400" /> Closed</label>
            {!s.closed && <>
              <input type="time" value={s.open || "11:00"} onChange={(e) => setSpecial(i, "open", e.target.value)} className="bg-[#101311] rounded-lg px-3 py-2 text-sm border border-emerald-800/50 outline-none focus:border-amber-400" />
              <span className="text-[#FDFBF7]/50">to</span>
              <input type="time" value={s.close || "21:00"} onChange={(e) => setSpecial(i, "close", e.target.value)} className="bg-[#101311] rounded-lg px-3 py-2 text-sm border border-emerald-800/50 outline-none focus:border-amber-400" />
            </>}
            <input value={s.note || ""} onChange={(e) => setSpecial(i, "note", e.target.value)} placeholder="Note (e.g. July 4th)" className="flex-1 min-w-[120px] bg-[#101311] rounded-lg px-3 py-2 text-sm border border-emerald-800/50 outline-none focus:border-amber-400" />
            <button onClick={() => rmSpecial(i)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={addSpecial} className="text-sm text-amber-300 flex items-center gap-1"><Plus className="w-4 h-4" /> Add special date</button>
      </Card>

      <Card className="space-y-3">
        <h3 className="font-display font-black uppercase text-amber-400">Settings</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <L t="Time Zone">
            <select value={cfg.timezone} onChange={(e) => setCfg((p) => ({ ...p, timezone: e.target.value }))} data-testid="admin-timezone" className={inp}>
              {["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu"].map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </L>
          <L t="'Closing Soon' threshold (minutes)"><input type="number" value={cfg.closing_soon_minutes} onChange={(e) => setCfg((p) => ({ ...p, closing_soon_minutes: Number(e.target.value) }))} data-testid="admin-closing-soon" className={inp} /></L>
        </div>
      </Card>

      <button data-testid="admin-save-hours" onClick={() => persist(null)} disabled={saving} className="w-full py-3.5 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-50 text-[#0A2818] font-display font-black uppercase tracking-wide flex items-center justify-center gap-2">
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save Changes
      </button>
    </div>
  );
}

function ImageUploadField({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success("Photo uploaded — click Save All Settings to publish");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-amber-400/80 font-bold mb-2">{label}</div>
      <div className="flex items-start gap-4">
        {value
          ? <img src={value} alt="preview" data-testid="admin-chef-image-preview" className="w-20 h-24 rounded-xl object-cover border border-amber-500/40 shrink-0" />
          : <div className="w-20 h-24 rounded-xl bg-[#101311] border border-dashed border-emerald-800/50 grid place-items-center text-emerald-700 shrink-0"><ImageIcon className="w-6 h-6" /></div>}
        <div className="flex-1 space-y-2">
          <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-700 hover:bg-emerald-600 text-sm font-display font-bold uppercase tracking-wide cursor-pointer transition-colors" data-testid={testid}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {uploading ? "Uploading…" : "Upload Photo"}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handle} disabled={uploading} />
          </label>
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="…or paste an image URL" className={inp} data-testid="admin-chef-image-url" />
        </div>
      </div>
    </div>
  );
}

function SettingsTab() {
  const { settings, reload } = useData();
  const [f, setF] = useState(null);
  useEffect(() => { if (settings) setF({ ...settings }); }, [settings]);
  if (!f) return <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto mt-10" />;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setHours = (i, text) => setF((p) => ({ ...p, hours: p.hours.map((h, idx) => idx === i ? { ...h, text } : h) }));
  const setLoyalty = (k, v) => setF((p) => ({ ...p, loyalty: { ...(p.loyalty || {}), [k]: v } }));
  const updArr = (key, i, field, v) => setF((p) => ({ ...p, [key]: (p[key] || []).map((it, idx) => idx === i ? { ...it, [field]: v } : it) }));
  const addArr = (key, blank) => setF((p) => ({ ...p, [key]: [...(p[key] || []), blank] }));
  const rmArr = (key, i) => setF((p) => ({ ...p, [key]: (p[key] || []).filter((_, idx) => idx !== i) }));
  const save = async () => {
    if (!(f.address || "").trim()) { toast.error("Business address can't be empty"); return; }
    await api.put("/admin/settings", { ...f, address: f.address.trim(), tax_rate: Number(f.tax_rate), delivery_fee: Number(f.delivery_fee) });
    await reload();
    toast.success("Settings saved");
  };
  return (
    <div className="space-y-4">
      <Card className="space-y-3">
        <h3 className="font-display font-black uppercase text-amber-400">Business</h3>
        <L t="Business Address"><input value={f.address || ""} onChange={(e) => set("address", e.target.value)} placeholder="e.g. 135 Congress St, Portland, ME 04101" className={inp} data-testid="admin-settings-address" /></L>
        <L t="Phone"><input value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inp} data-testid="admin-settings-phone" /></L>
        <L t="Cash App"><input value={f.cashapp || ""} onChange={(e) => set("cashapp", e.target.value)} className={inp} /></L>
        <L t="Service Area Note"><input value={f.service_area_note || ""} onChange={(e) => set("service_area_note", e.target.value)} className={inp} /></L>
        <L t="Booking Note"><input value={f.booking_note || ""} onChange={(e) => set("booking_note", e.target.value)} className={inp} /></L>
        <div className="grid grid-cols-2 gap-3">
          <L t="Tax Rate (e.g. 0.08)"><input type="number" step="0.001" value={f.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} className={inp} data-testid="admin-settings-tax" /></L>
          <L t="Default Delivery Fee"><input type="number" step="0.01" value={f.delivery_fee} onChange={(e) => set("delivery_fee", e.target.value)} className={inp} /></L>
        </div>
        <div className="flex gap-5">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.pickup_enabled} onChange={(e) => set("pickup_enabled", e.target.checked)} className="accent-amber-400" /> Pickup Enabled</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.delivery_enabled} onChange={(e) => set("delivery_enabled", e.target.checked)} className="accent-amber-400" /> Delivery Enabled</label>
        </div>
        <label data-testid="admin-settings-ai-toggle" className="flex items-start gap-3 rounded-xl border border-emerald-800/50 p-3 cursor-pointer">
          <input data-testid="admin-settings-ai-checkbox" type="checkbox" checked={f.ai_enabled === true} onChange={(e) => set("ai_enabled", e.target.checked)} className="accent-amber-400 mt-1" />
          <span><span className="font-bold text-sm">AI features (OpenAI) on</span><span className="block text-xs text-[#FDFBF7]/50">Turns the customer "Ask Us" chat and all "Write with AI" buttons on or off. Save to apply.</span></span>
        </label>
      </Card>

      <Card className="space-y-3">
        <h3 className="font-display font-black uppercase text-amber-400">Hero & Story</h3>
        <L t="Hero Headline 1"><input value={f.hero_headline_1 || ""} onChange={(e) => set("hero_headline_1", e.target.value)} className={inp} /></L>
        <L t="Hero Headline 2"><input value={f.hero_headline_2 || ""} onChange={(e) => set("hero_headline_2", e.target.value)} className={inp} /></L>
        <L t="Hero Subheadline"><input value={f.hero_subheadline || ""} onChange={(e) => set("hero_subheadline", e.target.value)} className={inp} /></L>
        <L t="Hero Image URL"><input value={f.hero_image || ""} onChange={(e) => set("hero_image", e.target.value)} className={inp} /></L>
        <ImageUploadField label="Story / Chef Photo" value={f.chef_image} onChange={(v) => set("chef_image", v)} testid="admin-chef-image-upload" />
        <L t="Story Title"><input value={f.story_title || ""} onChange={(e) => set("story_title", e.target.value)} className={inp} /></L>
        <L t="Story Body"><textarea rows={5} value={f.story_body || ""} onChange={(e) => set("story_body", e.target.value)} className={inp} /></L>
      </Card>

      <Card className="space-y-3">
        <h3 className="font-display font-black uppercase text-amber-400">Business Hours</h3>
        {(f.hours || []).map((h, i) => (
          <div key={h.day} className="flex items-center gap-3">
            <span className="w-28 text-sm font-semibold">{h.day}</span>
            <input value={h.text} onChange={(e) => setHours(i, e.target.value)} placeholder="e.g. 11am – 9pm (or leave blank for closed)" className={inp} />
          </div>
        ))}
        <L t="Holiday / Special Hours Note"><input value={f.holiday_hours_note || ""} onChange={(e) => set("holiday_hours_note", e.target.value)} className={inp} /></L>
      </Card>

      <Card className="space-y-3">
        <h3 className="font-display font-black uppercase text-amber-400">Catering Packages</h3>
        {(f.catering_packages || []).map((p, i) => (
          <div key={i} className="grid grid-cols-2 gap-2 border-b border-emerald-900/40 pb-3">
            <input value={p.name || ""} onChange={(e) => updArr("catering_packages", i, "name", e.target.value)} placeholder="Package name" className={inp} />
            <input value={p.min_guests || ""} onChange={(e) => updArr("catering_packages", i, "min_guests", e.target.value)} placeholder="Min guests (e.g. 15+)" className={inp} />
            <input value={p.price || ""} onChange={(e) => updArr("catering_packages", i, "price", e.target.value)} placeholder="Price (optional)" className={inp} />
            <input value={p.note || ""} onChange={(e) => updArr("catering_packages", i, "note", e.target.value)} placeholder="Short note" className={inp} />
            <textarea value={p.includes || ""} onChange={(e) => updArr("catering_packages", i, "includes", e.target.value)} placeholder="What's included" className={`${inp} col-span-2`} rows={2} />
            <button onClick={() => rmArr("catering_packages", i)} className="col-span-2 text-red-400 text-xs text-left">Remove package</button>
          </div>
        ))}
        <button onClick={() => addArr("catering_packages", { name: "", min_guests: "", price: "", includes: "", note: "" })} className="text-amber-400 text-sm font-bold">+ Add Package</button>
      </Card>

      <Card className="space-y-3">
        <h3 className="font-display font-black uppercase text-amber-400">FAQs</h3>
        {(f.faqs || []).map((x, i) => (
          <div key={i} className="border-b border-emerald-900/40 pb-3 space-y-2">
            <input value={x.q || ""} onChange={(e) => updArr("faqs", i, "q", e.target.value)} placeholder="Question" className={inp} />
            <textarea value={x.a || ""} onChange={(e) => updArr("faqs", i, "a", e.target.value)} placeholder="Answer (leave blank if unknown)" className={inp} rows={2} />
            <button onClick={() => rmArr("faqs", i)} className="text-red-400 text-xs">Remove</button>
          </div>
        ))}
        <button onClick={() => addArr("faqs", { q: "", a: "" })} className="text-amber-400 text-sm font-bold">+ Add FAQ</button>
      </Card>

      <Card className="space-y-3">
        <h3 className="font-display font-black uppercase text-amber-400">Loyalty Rewards</h3>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.loyalty?.enabled} onChange={(e) => setLoyalty("enabled", e.target.checked)} className="accent-amber-400" data-testid="admin-loyalty-enabled" /> Enable "buy N plates, get 1 free" rewards</label>
        <L t="Plates needed for a free plate"><input type="number" min="2" value={f.loyalty?.threshold ?? 5} onChange={(e) => setLoyalty("threshold", Number(e.target.value))} className={inp} data-testid="admin-loyalty-threshold" /></L>
        <L t="Reward text (shown on site & checkout)"><input value={f.loyalty?.reward_text || ""} onChange={(e) => setLoyalty("reward_text", e.target.value)} className={inp} /></L>
      </Card>

      <Card className="space-y-3">
        <h3 className="font-display font-black uppercase text-amber-400">Social</h3>
        <div className="grid grid-cols-3 gap-3">
          <L t="Instagram"><input value={f.social?.instagram || ""} onChange={(e) => set("social", { ...f.social, instagram: e.target.value })} className={inp} /></L>
          <L t="TikTok"><input value={f.social?.tiktok || ""} onChange={(e) => set("social", { ...f.social, tiktok: e.target.value })} className={inp} /></L>
          <L t="Snapchat"><input value={f.social?.snapchat || ""} onChange={(e) => set("social", { ...f.social, snapchat: e.target.value })} className={inp} /></L>
        </div>
      </Card>

      <button data-testid="admin-settings-save" onClick={save} className="w-full py-4 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase tracking-wide flex items-center justify-center gap-2 sticky bottom-4"><Save className="w-5 h-5" /> Save All Settings</button>
    </div>
  );
}

function AccountTab() {
  const { user } = useAuth();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 6) return toast.error("New password must be at least 6 characters");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated");
      setCur(""); setNw("");
    } catch (e2) {
      toast.error(formatApiError(e2?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Card className="max-w-md">
      <h3 className="font-display font-black uppercase text-amber-400 mb-1">Account</h3>
      <p className="text-sm text-[#FDFBF7]/60 mb-4">Signed in as {user.email} · role: {user.role}</p>
      <form onSubmit={submit} className="space-y-3">
        <input data-testid="account-current-password" type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password" className={inp} />
        <input data-testid="account-new-password" type="password" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="New password (min 6 characters)" className={inp} />
        <button data-testid="account-change-password" disabled={busy} className="w-full py-3 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase tracking-wide disabled:opacity-50">{busy ? "Saving…" : "Change Password"}</button>
      </form>
    </Card>
  );
}

function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [f, setF] = useState({ email: "", name: "", password: "" });
  const load = useCallback(async () => { const r = await api.get("/admin/staff"); setStaff(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!f.email || !f.name || !f.password) return toast.error("All fields required");
    try { await api.post("/admin/staff", f); toast.success("Staff member added"); setF({ email: "", name: "", password: "" }); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const del = async (u) => { if (!window.confirm(`Remove ${u.name}?`)) return; await api.delete(`/admin/staff/${u.user_id}`); toast.success("Removed"); load(); };
  return (
    <div>
      <Card className="space-y-3 mb-4 max-w-md">
        <h3 className="font-display font-black uppercase text-amber-400">Add Staff</h3>
        <p className="text-xs text-[#FDFBF7]/50">Staff can view & update orders and catering — but not menu, settings, promotions, reviews or staff.</p>
        <input data-testid="staff-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" className={inp} />
        <input data-testid="staff-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" className={inp} />
        <input data-testid="staff-password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Temporary password (min 6 chars)" className={inp} />
        <button data-testid="staff-add" onClick={add} className="w-full py-3 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase tracking-wide">Add Staff Member</button>
      </Card>
      <div className="space-y-2">
        {staff.map((u) => (
          <Card key={u.user_id} className="flex justify-between items-center">
            <div><div className="font-bold">{u.name}</div><div className="text-sm text-[#FDFBF7]/60">{u.email}</div></div>
            <button data-testid={`staff-delete-${u.user_id}`} onClick={() => del(u)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
          </Card>
        ))}
        {!staff.length && <p className="text-[#FDFBF7]/50 text-sm">No staff yet. Add your first team member above.</p>}
      </div>
    </div>
  );
}

const L = ({ t, children }) => <label className="block"><span className="block text-xs uppercase tracking-wider text-[#FDFBF7]/50 font-bold mb-1">{t}</span>{children}</label>;
